// paex-delivery-method.js — the Availability - PE PDP block element (Store
// Availability UX v2, 2026-07-26). Requires paex-store-core.js + paex-edd.js
// loaded first (same-document deferred order). Ascent conventions throughout:
// webvista helpers, PUB_SUB_EVENTS, AbortController teardown, light DOM.

if (!customElements.get('paex-delivery-method')) {
  customElements.define(
    'paex-delivery-method',
    class PaexDeliveryMethod extends HTMLElement {
      connectedCallback() {
        if (this.dataset.originalSection) return; // quick-view clone — native guard pattern
        this.abortController = new AbortController();

        const embed = this.querySelector('script[data-paex-delivery-data]');
        try {
          this.data = embed ? JSON.parse(embed.textContent) : null;
        } catch (error) {
          this.data = null;
        }
        if (!this.data) return;
        // same ingestion rule as the header chip (fresh design 3a): a popup past
        // its closing_date is dropped here too, so the PDP drawer and the header
        // drawer list the same stores (Sasha 2026-10-10: one drawer, one state)
        this.data.stores = paexStorePruneExpired(this.data.stores);
        this.displayNames = paexDeliveryDisplayNames(this.data.stores);

        this.storeId = null;
        this.storeIsExplicit = false;
        // B4 (round 2): how the current store got here — saved | rcc |
        // account | postal | geo | gps | picked (analytics + re-pick rules)
        this.storeSource = null;
        this.geoFromGps = false;
        this.forceFootprint = this.hasAttribute('data-force-footprint'); // editor/QA
        this.inFootprint = this.hasAttribute('data-in-footprint'); // pre-paint cache
        this.gateResolved = false;
        this.variantId = Number(this.dataset.variantId) || null;
        // BOPIS (2026-10-22, Track 3.2): the page-local service choice ("Buy online"
        // rows), whether the shopper made it, the resolved store's distance (the
        // per-store pickup radius test) and the variant title for "in {variant}"
        this.selectedService = null;
        this.serviceChosen = false;
        this.freeShipGated = false; // polish: [data-freeship] hidden by the selected pickup / same-day row
        this.storeKm = null;
        this.localEditing = false;
        this.liveVariants = null;
        const paexDmVariantLabel = this.querySelector('[data-variant-label]');
        this.variantTitle = paexDmVariantLabel ? paexDmVariantLabel.textContent.trim() : '';

        // M7 (2026-09-02, false-OOS fix): live sellability revalidation.
        // The embed's "_a" flags are baked at page render — a cached or
        // long-open PDP can go stale. One no-store read of the product's own
        // .js endpoint returns variant.available live (the same sellability
        // signal the ATC button reads — qty-level rejections can still differ);
        // the map overrides the embed for every later render. Fail-silent:
        // on any failure the embed flags stand.
        this.liveAvail = null;
        const paexDmHandle = this.dataset.productHandle;
        if (paexDmHandle) {
          const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
          fetch(`${root}products/${paexDmHandle}.js`, { cache: 'no-store', priority: 'low', signal: this.abortController.signal })
            .then((response) => (response.ok ? response.json() : null))
            .then((productJson) => {
              if (!productJson || !Array.isArray(productJson.variants) || !this.isConnected) return;
              const map = {};
              productJson.variants.forEach((v) => { map[String(v.id)] = !!v.available; });
              // perf (review M7): repaint only when live truth CHANGES the
              // current variant's state — the common fresh-data case is a
              // no-op (no reconcile render, no late layout shift). Later
              // renders (variant change, countdown) read the map regardless.
              // Single-source ladder: onlineAvailable() before (liveAvail
              // still null → embed path) vs after the map lands.
              const before = this.onlineAvailable();
              this.liveAvail = map;
              // BOPIS: the same payload carries each variant's option values — the
              // size pills' per-service state (stampPills) reads it
              this.liveVariants = productJson.variants;
              this.stampPills();
              if (!this.variantId || this.onlineAvailable() === before) return;
              // a11y (review M7): the reconcile can hide the element the user is
              // standing on (notify button, flip CTA) — same guard as setStore()
              const hadFocus = this.contains(document.activeElement);
              this.render(); // reconcile the current variant against live truth
              if (hadFocus && !this.contains(document.activeElement)) {
                // round 2: under the selector the tile's own toggle is hidden — the ship row's chevron stands in
                const fallback = this.querySelector('[data-service="ship"] [data-tile-toggle]:not([hidden])') ||
                  this.querySelector('[data-tile="ship"] [data-tile-toggle]');
                if (fallback) fallback.focus();
              }
              this.announceAvail(); // the pill follows the live flip too
            })
            .catch(() => {});
        }
        this.pickupEnabled = this.dataset.enablePickup === 'true';
        this.tierLow = Number(this.dataset.tierLow) || 3;
        this.tierTop = Number(this.dataset.tierTop) || 10;
        this.radiusKm = Number(this.dataset.radius) || 75;
        this.restock = (this.dataset.restock || '').trim();
        this.restockEta = (this.dataset.restockEta || '').trim();
        this.inOccasion = this.dataset.inOccasion === 'true';
        this.analytics = this.dataset.analytics === 'true';
        this.eddLive = this.hasAttribute('data-edd-live');
        this.eddProxy = this.dataset.eddProxy || '/apps/paex-edd';
        // strict allowlist pins live-EDD fetches to one same-origin
        // app-proxy segment (same guard as the cart strip)
        if (!/^\/apps\/[A-Za-z0-9_-]+\/?$/.test(this.eddProxy)) this.eddProxy = '/apps/paex-edd';
        this.eddSource = this.dataset.eddSource || 'proxy';
        this.shopDomain = this.dataset.shop || (window.Shopify && Shopify.shop) || '';
        this.productId = this.dataset.productId || '';
        this.liveKeys = {}; // postal -> "YYYY-MM-DD" (carrier-live upgrade)
        this.livePending = {};
        this.expandFired = {};

        this.bindEvents();
        this.bindFulfil();
        if (this.pickupEnabled) this.resolveStore();
        else this.applyGate(false, true); // local only — never poison paex:gate
        this.render();
        // a11y (review M7): arm the ship status live region unconditionally
        // after hydration — armPoliteLive's own setTimeout(0) still keeps the
        // first paint silent, but the M7 reconcile flip (which may not change
        // the text until AFTER arming would have happened) now announces.
        const paexDmShipStatus = this.querySelector('[data-ship-status]');
        if (paexDmShipStatus) this.armPoliteLive(paexDmShipStatus);
        this.startCountdown();
        // Halloween clock - PE (2026-10-09): the shared clock (assets/paex-clock.js,
        // loaded at the end of body) drives the cutoff countdown while it is
        // active; with the clock off it never loads and nothing below runs
        if (window.paexClock) this.startClock();
        else document.addEventListener('paex:clock-ready', () => this.startClock(), { once: true, signal: this.abortController.signal });
      }

      disconnectedCallback() {
        if (this.abortController) this.abortController.abort();
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        if (this.countdownTimer) clearInterval(this.countdownTimer);
        if (this.preselectNoneTimer) clearTimeout(this.preselectNoneTimer);
      }

      bindEvents() {
        const signal = this.abortController.signal;

        document.addEventListener(
          PAEX_STORE_EVENT,
          (event) => {
            if (!event.detail || !event.detail.storeId) return;
            this.setStore(String(event.detail.storeId), true);
          },
          { signal }
        );

        // Tile summaries (mobile collapsed rows): one open at a time. Round 2 (lane A2): the
        // service rows carry their own chevron (outside the tile — aria-controls names the body)
        this.querySelectorAll('[data-tile-toggle]').forEach((toggle) => {
          toggle.addEventListener(
            'click',
            () => this.toggleTile(this.tileForToggle(toggle), true),
            { signal }
          );
        });
        // round 2 (review blocker): the Change store / Postal code openers inside the moved tiles stay live
        this.delegateOpeners(signal);

        // The ONLY in-block geolocation triggers — an explicit user tap (the
        // store tile prompt, and the location-backup line out of footprint).
        this.querySelectorAll('[data-use-location-inline]').forEach((locate) => {
          locate.addEventListener('click', () => this.useMyLocation(), { signal });
        });

        // Location backup (Sasha 2026-10-22): no location at all → offer it
        // (store line, pickup on) and ask for a postal instead of a date
        this.geoUnknown = window.paexGeoUnknown === true;
        document.addEventListener('paex:geo-unknown', () => {
          this.geoUnknown = true;
          this.syncGeoPrompt();
          this.render();
        }, { signal });
        document.addEventListener(PAEX_GEO_EVENT, (event) => {
          // a GPS fix ("Use my location") carries coordinates only — no
          // province/postal for a date, so the postal ask stays (WebKit QA:
          // it fell back to "Deliver to Canada · Arrives Tue, Nov 3")
          const detail = event.detail || {};
          if (!this.geoUnknown || !(detail.regionCode || detail.postal)) return;
          this.geoUnknown = false;
          this.syncGeoPrompt();
          this.render();
        }, { signal });
        document.addEventListener(PAEX_DEST_EVENT, () => this.syncGeoPrompt(), { signal });
        this.syncGeoPrompt();

        const recoverySwitch = this.querySelector('[data-recovery-switch]');
        if (recoverySwitch) {
          recoverySwitch.addEventListener(
            'click',
            () => {
              if (recoverySwitch.dataset.storeId) this.setStore(recoverySwitch.dataset.storeId, true);
            },
            { signal }
          );
        }

        // "Email me when it's back" proxies the back-in-stock trigger: the
        // merchant-configured app selector first (Swym — Sasha 2026-07-27),
        // then the theme's native notify form; buttons hide when neither exists.
        const notifySelector = (this.dataset.notifySelector || '').trim();
        const notifyTarget = () => {
          if (notifySelector) {
            try {
              const appTrigger = document.querySelector(notifySelector);
              if (appTrigger) return appTrigger;
            } catch (error) {
              // invalid selector typed in the setting — fall through
            }
          }
          return document.getElementById(`Notify-Email-Button-${this.dataset.section}`);
        };
        this.querySelectorAll('[data-notify-open], [data-notify-open-secondary]').forEach((button) => {
          // resolve lazily on click — app buttons often mount after us
          if (!notifySelector && !notifyTarget()) {
            button.hidden = true;
            return;
          }
          button.addEventListener('click', () => {
            const target = notifyTarget();
            if (target) target.click();
          }, { signal });
        });

        // Deliver-to line opens the inline postal editor (AfterShip-style);
        // a valid postal writes the destination and every date recomputes.
        const destOpen = this.querySelector('[data-dest-open]');
        const destForm = this.querySelector('[data-dest-form]');
        const destInput = this.querySelector('[data-dest-input]');
        if (destOpen && destForm && destInput) {
          destOpen.addEventListener('click', () => {
            if (this.postalSheet(destOpen)) return;
            destForm.hidden = false;
            destOpen.hidden = true;
            destInput.focus();
          }, { signal });
          const closeForm = () => {
            // lane A4 (visual review 6): under the Ship row the body IS the editor — the form stays open and the
            // "Deliver to … · Change" line stays hidden (the row's "· {place} Change" unit is the one opener)
            const inRow = this.tileInRow('ship');
            destForm.hidden = !inRow;
            destOpen.hidden = inRow;
            destInput.removeAttribute('aria-invalid');
            if (destError) destError.textContent = '';
          };
          // close on KEYUP: the house ModalDialog closes on keyup, so a
          // keydown-close would let the same press fall through to it
          // (same trap the store drawer already documents in core.js)
          destInput.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') event.stopPropagation();
          }, { signal });
          destInput.addEventListener('keyup', (event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              closeForm();
              destOpen.focus();
            }
          }, { signal });
          const destError = this.querySelector('[data-dest-error]');
          destForm.addEventListener('submit', (event) => {
            event.preventDefault();
            const raw = destInput.value.trim().toUpperCase();
            const usZip = /^\d{5}(-\d{4})?$/.test(raw);
            const region = usZip
              ? 'US'
              : (typeof paexProvinceFromPostal === 'function' ? paexProvinceFromPostal(raw) : null);
            const fullPostal = usZip || /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/.test(raw);
            const valid = fullPostal && region && (usZip || (typeof paexPostalKeys === 'function' && paexPostalKeys(raw)));
            if (!valid) {
              destInput.setAttribute('aria-invalid', 'true');
              if (destError) destError.textContent = this.data.texts.destError || '';
              destInput.focus();
              return;
            }
            destInput.removeAttribute('aria-invalid');
            if (destError) destError.textContent = '';
            const spaced = raw.replace(/\s+/g, '');
            const place = usZip
              ? raw
              : (spaced.length > 3 ? `${spaced.slice(0, 3)} ${spaced.slice(3)}` : spaced);
            paexDestWrite({ region: region, place: place });
            // BOPIS (6c): the PDP editor also writes the cart's edd_postal (bridge helper, feature-detected)
            this.writeEddPostal(place, !usZip);
            closeForm();
            destOpen.focus();
          }, { signal });
        }

        // lane A3 (round 2): the Ship row's "· {place} Change" link (a real button, moved into the row's live
        // line by placeTile) opens the ONE Deliver-to editor — openPostalEditor: ship body open under the row,
        // the editor's input focused — never a second editor
        const shipDestOpen = this.querySelector('[data-ship-dest-open]');
        if (shipDestOpen) {
          shipDestOpen.addEventListener('click', (event) => {
            event.preventDefault();
            this.openPostalEditor();
          }, { signal });
        }

        // C13 revised: nearby rows (Select = an explicit pick, like the
        // recovery Switch) and See all (the drawer); delegated — JS rebuilds rows
        const nearbyRows = this.querySelector('[data-nearby-rows]');
        if (nearbyRows) {
          nearbyRows.addEventListener(
            'click',
            (event) => {
              const pick = event.target.closest('[data-store-id]');
              if (pick && nearbyRows.contains(pick)) this.setStore(pick.dataset.storeId, true);
            },
            { signal }
          );
        }
        const seeAll = this.querySelector('[data-see-all]');
        if (seeAll) {
          seeAll.addEventListener('click', () => this.openDrawer(), { signal });
        }

        const moreStores = this.querySelector('[data-more-stores]');
        if (moreStores) {
          moreStores.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              this.openDrawer();
            },
            { signal }
          );
        }

        this.variantChangeUnsubscriber = webvista.subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          try {
            this.liveKeys = {};
            this.livePending = {};
          if (event.data.sectionId !== this.dataset.section) return;
          if (!event.data.variant) return; // engine never publishes for unavailable combos
          this.variantId = event.data.variant.id;
          const title = event.data.variant.title;
          this.variantTitle = title && title !== 'Default Title' ? title : '';
          if (title && title !== 'Default Title') {
            const label = this.querySelector('[data-variant-label]');
            if (label) label.textContent = title;
            const drawerContext = this.querySelector('[data-drawer-variant]');
            if (drawerContext) drawerContext.textContent = ` · ${title}`;
          }
          // BOPIS: the native resync (global.js toggleAddButtonStatus, then
          // paex-variant-fast) rewrote the button from the source html BEFORE
          // this publish — read that native state, then render() re-applies the
          // service override on top (renderServices → applyButton)
          this.captureCtaNative(event.data.html);
          this.render();
          const drawer = this.querySelector('paex-store-drawer');
          if (drawer && typeof drawer.refreshRows === 'function') drawer.refreshRows();
          this.announceAvail(); // the pill renders off this element's new state
          // once more on a microtask: anything queued after us in the same task
          // (seams §1 step 2) lands before this re-apply
          Promise.resolve().then(() => {
            if (!this.isConnected) return;
            this.applyButton();
            this.stampPills();
          });
          } catch (error) {
            /* publish loop has no per-callback try/catch */
          }
        });

        // Typed postal/city from the selector = the delivery destination:
        // recompute dates, free shipping and the badge for the new place.
        document.addEventListener(
          PAEX_DEST_EVENT,
          (event) => {
            this.applyDest(event.detail); // safety net: may promote the gate
            this.renderShipping();
            this.renderFreeShipping();
            this.renderSummaries();
            this.renderServices(); // BOPIS: the same-day row follows the typed postal
          },
          { signal }
        );

        // zone / cart state from the free shipping offer engine (one state for
        // the drawer, this block and the announcement bar)
        document.addEventListener(
          'paex:offer-change',
          () => {
            this.renderFreeShipping();
            this.renderHeading();
          },
          { signal }
        );
      }

      /* ---------------- inventory provider (tiered, capped) ---------------- */

      qtyFor(variantId, locationId) {
        const variantStock = this.data.variants[String(variantId)];
        if (!variantStock) return 0;
        const qty = Number(variantStock[String(locationId)]);
        return Number.isFinite(qty) && qty > 0 ? Math.min(qty, this.tierTop) : 0;
      }

      // 'out' | 'only' (≤ low tier) | 'some' | 'top' (= capped max)
      tierFor(qty) {
        if (qty <= 0) return 'out';
        if (qty <= this.tierLow) return 'only';
        return qty >= this.tierTop ? 'top' : 'some';
      }

      tierText(qty) {
        const texts = this.data.texts;
        const tier = this.tierFor(qty);
        if (tier === 'out') return texts.tierOut;
        if (tier === 'only') return paexDeliveryFormat(texts.tierOnly, { n: qty });
        if (tier === 'top') return paexDeliveryFormat(texts.tierTop, { top: this.tierTop });
        return paexDeliveryFormat(texts.tierSome, { n: qty });
      }

      tierTextFor(variantId, locationId) {
        return this.tierText(this.qtyFor(variantId, locationId));
      }

      tierStateFor(variantId, locationId) {
        return this.tierFor(this.qtyFor(variantId, locationId));
      }

      // (v1 drawer stock contract removed 2026-07-29 — every live host
      // implements tierTextFor/tierStateFor; see paex-store-core stock reads)

      /* --------------- store + footprint resolution (fail closed) --------------- */

      resolveStore() {
        // P3 (round 2): same cookie order as the header chip — our saved
        // store first, then the RCC app's place id
        const savedCookie = webvista.getCookie(PAEX_STORE_COOKIE);
        const fromCookie = savedCookie || webvista.getCookie(PAEX_RCC_STORE_COOKIE);
        if (fromCookie && paexDeliveryHasStore(this.data.stores, fromCookie)) {
          this.storeId = fromCookie;
          this.storeIsExplicit = true;
          this.storeSource = fromCookie === savedCookie ? 'saved' : 'rcc';
          this.applyGate(true); // a chosen store ALWAYS wins the gate
          this.reportPreselect(this.storeSource, fromCookie, null);
          return;
        }
        const preferred = this.dataset.preferredStoreId;
        if (preferred && paexDeliveryHasStore(this.data.stores, preferred)) {
          this.setStore(preferred, true, true); // passive resolution — no event
          this.storeSource = 'account';
          this.reportPreselect('account', preferred, null);
          return;
        }
        // Postal-code safety net (Sasha 2026-10-12): a typed destination from
        // this session (paex:dest) may promote the gate — runs beside geo
        this.applyDest(paexDestRead());
        // no saved / account store: geo decides — report 'none' once geo has
        // had its chance (B4 analytics; never fires when a store is set)
        this.schedulePreselectNone();

        // 1e resolution: address on file OUTRANKS geo-IP, and it gates by
        // the postal's real distance (FSA centroid), falling back to the
        // province match, then to geo. Fail closed throughout.
        const customerZip = String(this.dataset.customerZip || '').replace(/\s+/g, '').toUpperCase();
        const customerRegion = (this.dataset.customerRegion || '').trim();
        if (customerZip && /^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(customerZip)) {
          const fsaUrl = this.fsaUrl();
          if (fsaUrl) {
            // P2: while the centroid is pending, IP geo may not pick the store
            // (the address on file outranks it); it still personalizes the
            // ship side
            this.postalPending = true;
            fetch(fsaUrl)
              .then((response) => (response.ok ? response.json() : null))
              .then((centroids) => {
                this.postalPending = false;
                if (this.storeIsExplicit) return; // a chosen store always wins
                // dataset shape: { fsa: { "H7P": [lat,lng], "G4": [...] } } —
                // walk the prefix keys longest-first (paexPostalKeys)
                const table = (centroids && centroids.fsa) || {};
                const keys = (typeof paexPostalKeys === 'function' && paexPostalKeys(customerZip)) || [customerZip.slice(0, 3)];
                let hit = null;
                for (const key of keys) {
                  if (table[key]) { hit = table[key]; break; }
                }
                if (hit && Number.isFinite(hit[0]) && Number.isFinite(hit[1])) {
                  const fit = paexFootprint(
                    { lat: hit[0], lng: hit[1], regionCode: paexProvinceFromPostal(customerZip) || customerRegion },
                    this.data.stores,
                    this.radiusKm
                  );
                  this.applyGate(fit.inFootprint);
                  // P2: the store comes from the address too (nearest to the
                  // FSA centroid, within the radius) — replaces only a geo pick
                  if (fit.inFootprint && fit.nearestId && (!this.storeId || this.storeSource === 'geo')) {
                    this.storeId = fit.nearestId; // NOT persisted
                    this.storeKm = fit.nearestKm; // BOPIS: the pickup-radius test measures from the FSA centroid
                    this.storeSource = 'postal';
                    this.reportPreselect('postal', fit.nearestId, fit.nearestKm);
                    this.render();
                  }
                } else {
                  this.gateFromRegionOrGeo(customerRegion);
                }
                // 2. gate just resolved — geo may have arrived first; give the
                // nearest-store suggestion its retry
                if (this.inFootprint && !this.storeId) {
                  const lateGeo = paexDeliveryReadGeo();
                  if (lateGeo) this.applyGeo(lateGeo, true);
                }
              })
              .catch(() => {
                this.postalPending = false;
                if (!this.storeIsExplicit) this.gateFromRegionOrGeo(customerRegion);
              });
            // geo still enriches the DESTINATION + nearest store; the gate is
            // ours. The cache re-dispatches before deferred scripts run, so
            // read it directly too.
            const cachedGeo = paexDeliveryReadGeo();
            if (cachedGeo) this.applyGeo(cachedGeo, true);
            this.listenGeo(true);
            return;
          }
        }
        const geo = paexDeliveryReadGeo();
        this.listenGeo(false);
        if (geo) {
          this.applyGeo(geo);
          return;
        }
        if (customerRegion && !this.gateResolved) {
          const fit = paexFootprint({ regionCode: customerRegion }, this.data.stores, this.radiusKm);
          this.applyGate(fit.inFootprint);
        }
      }

      fsaUrl() {
        const drawer = document.querySelector('[data-fsa-url]');
        return (this.dataset.fsaUrl || (drawer && drawer.dataset.fsaUrl) || '').trim() || null;
      }

      gateFromRegionOrGeo(customerRegion) {
        if (this.gateResolved) return;
        if (customerRegion) {
          const fit = paexFootprint({ regionCode: customerRegion }, this.data.stores, this.radiusKm);
          this.applyGate(fit.inFootprint);
          if (this.inFootprint && !this.storeId) {
            const lateGeo = paexDeliveryReadGeo();
            if (lateGeo) this.applyGeo(lateGeo, true);
          }
          return;
        }
        const geo = paexDeliveryReadGeo();
        if (geo) this.applyGeo(geo);
      }

      setStore(storeId, persist, quiet) {
        if (!paexDeliveryHasStore(this.data.stores, storeId)) return;
        const changed = this.storeId !== storeId;
        this.storeId = storeId;
        if (changed) this.storeKm = null; // BOPIS: a chosen store counts as inside its own radius
        this.storeIsExplicit = persist;
        if (persist) this.storeSource = 'picked';
        this.applyGate(true);
        if (persist && !window.Shopify?.designMode) {
          webvista.setCookie(PAEX_STORE_COOKIE, storeId, PAEX_STORE_COOKIE_HOURS);
          if (!quiet) {
            this.pushAnalytics('store_selected', { store_id: storeId });
            // every explicit selection path broadcasts — the header chip must
            // mirror a nearby-pill or recovery-switch click exactly like a
            // drawer pick (Sasha 2026-07-27). `changed` stops the echo loop:
            // our own PAEX_STORE_EVENT listener re-enters with the same id.
            if (changed) {
              document.dispatchEvent(new CustomEvent(PAEX_STORE_EVENT, { detail: { storeId } }));
            }
          }
        }
        this.render();
        // a Switch/pill click removes its own button — keep focus in the block
        const focusTarget = this.querySelector('[data-store-name]');
        if (persist && focusTarget && !this.contains(document.activeElement)) {
          focusTarget.focus();
        }
      }

      // keepGate: the customer-postal verdict already decided the gate —
      // geo only enriches the destination texts and the nearest store
      applyGeo(geo, keepGate) {
        if (typeof paexDeliveryUsableGeo === 'function' && !paexDeliveryUsableGeo(geo)) return;
        if (this.storeIsExplicit) {
          // a chosen store owns the GATE and the store tile, but late geo
          // still personalizes the ship side (deliver-to place, live EDD) —
          // the first visit must not need a refresh (Sasha 2026-07-28)
          this.render();
          return;
        }
        const fit = paexFootprint(geo, this.data.stores, this.radiusKm);
        if (!keepGate) this.applyGate(fit.inFootprint);
        // P2: under the address gate, the IP-nearest store must itself be
        // within the radius, and waits for the pending FSA centroid
        const eligible = keepGate
          ? this.inFootprint && !this.postalPending && fit.nearestKm != null && fit.nearestKm <= this.radiusKm
          : fit.inFootprint;
        if (eligible && fit.nearestId && !this.storeId) {
          this.storeId = fit.nearestId; // geo result is NOT persisted
          this.storeKm = fit.nearestKm; // BOPIS: the pickup-radius test measures from the geo position
          this.storeSource = this.geoFromGps ? 'gps' : 'geo';
          this.reportPreselect(this.storeSource, fit.nearestId, fit.nearestKm);
        }
        this.render();
      }

      // Postal-code safety net (Sasha 2026-10-12) — paexDestFootprint in
      // paex-store-core.js. Promote only: inside the radius → store version
      // for the session (sessionStorage paex:dest is the memory), nearest
      // store suggested like a geo pick (NOT persisted); outside → no change.
      applyDest(dest) {
        if (!this.pickupEnabled || this.storeIsExplicit || !paexDestResolvable(dest)) return;
        const seq = (this.destSeq = (this.destSeq || 0) + 1);
        this.destPending = true;
        paexDestFootprint(dest, this.data.stores, this.radiusKm, this.fsaUrl())
          .catch(() => null)
          .then((fit) => {
            if (seq !== this.destSeq) return;
            this.destPending = false;
            if (!this.isConnected) return;
            if (fit && fit.inFootprint && !this.storeIsExplicit) {
              this.destGate = true;
              this.deferredGate = null;
              if (!this.inFootprint) this.applyGate(true);
              if (fit.nearestId && (!this.storeId || this.storeSource === 'geo' || this.storeSource === 'gps')) {
                this.storeId = fit.nearestId;
                this.storeKm = fit.nearestKm; // BOPIS: typed postal → FSA centroid distance
                this.storeSource = 'dest';
                this.reportPreselect('dest', fit.nearestId, fit.nearestKm);
              }
              this.render();
              this.announceAvail();
              return;
            }
            // outside the radius / no verdict: replay the demotion we held back
            if (this.deferredGate) {
              const held = this.deferredGate;
              this.deferredGate = null;
              this.applyGate(false, held.skipWrite);
            }
          });
      }

      // P6: persistent (like the header chip) — a later fix, e.g. the store
      // drawer's "Use my location", re-picks; one listener per element
      listenGeo(keepGate) {
        if (this.geoListening) return;
        this.geoListening = true;
        document.addEventListener(
          PAEX_GEO_EVENT,
          (event) => this.onGeoReady(event.detail, keepGate),
          { signal: this.abortController.signal }
        );
      }

      onGeoReady(geo, keepGate) {
        if (this.ownGeoDispatch) return; // our own GPS rebroadcast, already applied
        if (geo && geo.source === 'gps') {
          // a precise fix the shopper asked for outranks IP / address picks
          if (!this.storeIsExplicit) this.storeId = null;
          this.geoFromGps = true;
          this.applyGeo(geo);
          return;
        }
        this.applyGeo(geo, keepGate);
      }

      applyGate(inFootprint, skipWrite) {
        // Postal-code safety net (Sasha 2026-10-12): a promoting typed
        // destination outranks every later verdict (IP geo, address on file);
        // while its lookup is pending a demotion waits (the pre-paint cache
        // already shows the store version — no OUT→IN flash on the next page)
        if (!inFootprint && !this.forceFootprint) {
          if (this.destGate) return;
          if (this.destPending) { this.deferredGate = { skipWrite: Boolean(skipWrite) }; return; }
        }
        this.inFootprint = this.forceFootprint || Boolean(inFootprint);
        const changed = !this.gateResolved;
        this.gateResolved = true;
        // forced (editor/QA) and pickup-off verdicts never pollute the cache
        if (!this.forceFootprint && !skipWrite) paexGateWrite(this.inFootprint);
        if (changed || this.hasAttribute('data-in-footprint') !== this.inFootprint) {
          this.toggleAttribute('data-in-footprint', this.inFootprint);
          this.pushAnalytics('in_footprint', { in_footprint: this.inFootprint });
        }
        this.syncGeoPrompt();
        this.render();
      }

      syncGeoPrompt() {
        const prompt = this.querySelector('[data-geo-prompt]');
        if (!prompt) return;
        // a typed destination answered the question, even outside the radius
        const typed = typeof paexDestRead === 'function' && paexDestRead();
        prompt.hidden = !(this.geoUnknown && !this.inFootprint && !this.storeIsExplicit && !typed);
      }

      useMyLocation() {
        if (!navigator.geolocation) {
          this.openDrawer();
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const coords = { lat: position.coords.latitude, lng: position.coords.longitude };
            const merged = Object.assign({}, paexDeliveryReadGeo(), coords);
            paexDeliveryWriteGeoCache(merged);
            // P1: a precise fix replaces an IP / address preselection (the
            // permission prompt gives IP geo time to land first)
            if (!this.storeIsExplicit) this.storeId = null;
            this.storeIsExplicit = false;
            this.geoFromGps = true;
            this.applyGeo(merged);
            // the chip's gate listens for this — same contract as the drawer
            this.ownGeoDispatch = true;
            try {
              document.dispatchEvent(new CustomEvent(PAEX_GEO_EVENT, { detail: Object.assign({ source: 'gps' }, merged) }));
            } finally {
              this.ownGeoDispatch = false;
            }
          },
          () => this.openDrawer() // denial changes nothing visible; offer the postal path
        );
      }

      openDrawer() {
        // the first VISIBLE opener (focus returns to it on close); the
        // sub-line Change store opener is hidden while no store is set
        const openers = Array.from(this.querySelectorAll('modal-opener[aria-controls]'));
        const opener = openers.find((each) => each.getClientRects().length > 0) || openers[0];
        const drawerId = opener && opener.getAttribute('aria-controls');
        const drawer = drawerId && document.getElementById(drawerId);
        if (drawer && typeof drawer.show === 'function') {   // D13-3: open attr now, trapFocus/scroll lock next frame
          drawer.setAttribute('open', '');
          if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => drawer.show(opener)); else drawer.show(opener);
        }
      }

      /* ---------------- delivery date + destination ---------------- */

      // Destination resolution: typed postal/city (selector) → customer
      // address on file → geo-IP region → unknown (no promise).
      destRegion() {
        // NOTE (Sasha 2026-07-28): the chosen store must NEVER influence the
        // ship side — no store step in this chain
        const dest = paexDestRead();
        if (dest) return String(dest.region || '').trim().toUpperCase() || null;
        const customerRegion = (this.dataset.customerRegion || '').trim().toUpperCase();
        if (customerRegion) return customerRegion;
        const geo = paexDeliveryReadGeo();
        const region = geo && String(geo.regionCode || '').trim().toUpperCase();
        return region || null;
      }

      destPlace() {
        const dest = paexDestRead();
        if (dest && dest.place) return dest.place;
        // detected location leads with the most personal signal we have —
        // postal, then city, then province (Sasha 2026-07-28: show the
        // customer THEIR place first; Change is for when we got it wrong)
        const geo = paexDeliveryReadGeo();
        if (geo && geo.postal) return geo.postal;
        if (geo && geo.city) return geo.city;
        if (geo && geo.regionName) return geo.regionName;
        return this.destRegion();
      }

      // Postal for the LIVE provider: a typed postal beats the customer's
      // saved one, which beats the geo-detected postal (Sasha 2026-07-28 —
      // anonymous visitors get live dates + the badge without typing).
      destPostal() {
        const dest = paexDestRead();
        // a chosen destination (postal OR city) owns the live provider — the
        // fallbacks only speak when nothing was chosen
        const geo = paexDeliveryReadGeo();
        const candidates = dest
          ? [dest.place]
          : [this.dataset.customerZip, geo && geo.postal];
        for (const value of candidates) {
          const clean = String(value || '').replace(/\s+/g, '').toUpperCase();
          // CA full postal OR bare FSA — geo-IP resolves 3 chars and the
          // prediction API accepts them (verified live 2026-07-28)
          if (/^[A-Z]\d[A-Z](\d[A-Z]\d)?$/.test(clean)) return clean;
          if (/^\d{5}(-\d{4})?$/.test(clean)) return clean; // US
        }
        return null;
      }

      estimate() {
        // no destination -> the '*' wildcard row (most of Canada) answers, so
        // the ship side ALWAYS has a date when in stock (Sasha 2026-07-28);
        // the badge/get-it-by guards still key on the REAL destination
        const realRegion = this.destRegion();
        // Sasha 2026-10-22 (supersedes 2026-07-28 for this case): location
        // confirmed unknown → no '*' date (6 days read "after Halloween" to
        // Quebec shoppers); the deliver-to foot asks for a postal instead
        if (!realRegion && this.geoUnknown) return null;
        const region = realRegion || '*';
        if (typeof paexEddEstimate !== 'function') return null;
        const base = paexEddEstimate(region, this.data.settings.edd);
        if (!base) return null;
        base.generic = !realRegion;
        // Halloween clock - PE (2026-10-09): a live date fetched for the
        // morning slot must not survive the cutoff (honesty) — while the clock
        // runs, the live memo is dropped whenever the order day rolls over
        if (this.clockActive()) {
          const orderDay = paexEddKey(base.orderDay.getUTCFullYear(), base.orderDay.getUTCMonth() + 1, base.orderDay.getUTCDate());
          if (this.liveOrderDay && this.liveOrderDay !== orderDay) {
            this.liveKeys = {};
            this.livePending = {};
          }
          this.liveOrderDay = orderDay;
        }
        // carrier-live upgrade in place (design 1g: never block, upgrade)
        const postal = this.destPostal();
        const lookup = this.liveLookupKey(postal);
        const liveEntry = lookup && this.liveKeys[lookup];
        if (liveEntry && Date.now() - liveEntry.t >= 1800000) {
          delete this.liveKeys[lookup]; // page outlived the TTL — refetch
        }
        const freshEntry = lookup && this.liveKeys[lookup];
        const liveOk = freshEntry && freshEntry.k &&
          (typeof paexEddLiveAccepts !== 'function' || paexEddLiveAccepts(base, freshEntry.k));
        if (liveOk) {
          return Object.assign({}, base, {
            arrivalKey: freshEntry.k,
            arrival: new Date(`${freshEntry.k}T12:00:00Z`),
            live: true,
          });
        }
        this.upgradeLive(postal);
        return base;
      }

      // app mode reaches AfterShip without a postal (their backend
      // IP-geolocates); proxy mode stays postal-gated
      liveLookupKey(postal) {
        if (postal) return `${this.variantId || 'v'}:${postal}`;
        if (this.eddSource === 'app') {
          return `${this.variantId || 'v'}:${this.destRegion() || 'ip'}`;
        }
        // no postal-grade destination anywhere: geo city+region still gets
        // a live prediction (Sasha 2026-07-28 — widget-grade experience)
        const geo = paexDeliveryReadGeo();
        if (geo && geo.city && geo.regionCode) {
          return `${this.variantId || 'v'}:c:${geo.regionCode}:${geo.city}`;
        }
        return null;
      }

      upgradeLive(postal) {
        if (!this.eddLive) return;
        const lookup = this.liveLookupKey(postal);
        if (!lookup) return;
        if (this.liveKeys[lookup] !== undefined || this.livePending[lookup]) return;
        let fetcher = null;
        if (this.eddSource === 'app' && typeof paexEddAppFetch === 'function') {
          fetcher = paexEddAppFetch({
            shop: this.shopDomain,
            productId: this.productId,
            variantId: this.variantId,
            postal,
            region: this.destRegion(),
            country: this.destRegion() === 'US' ? 'US' : 'CA',
          });
        } else if (postal && typeof paexEddLiveFetch === 'function') {
          // processing days ride along so the carrier pickup AfterShip
          // predicts from matches the theme's dispatch day (Sasha 2026-09-21)
          fetcher = paexEddLiveFetch(this.eddProxy, postal, this.data.settings.edd.processing);
        } else if (typeof paexEddLiveFetchCity === 'function') {
          const geo = paexDeliveryReadGeo();
          if (geo && geo.city && geo.regionCode) {
            fetcher = paexEddLiveFetchCity(this.eddProxy, geo.city, geo.regionCode, geo.country || 'CA', this.data.settings.edd.processing);
          }
        }
        if (!fetcher) return;
        this.livePending[lookup] = true;
        fetcher.then((key) => {
          this.livePending[lookup] = false;
          this.liveKeys[lookup] = { k: key, t: Date.now() }; // null caches the miss too
          if (key) {
            this.renderShipping();
            this.renderSummaries();
            // BOPIS: the Ship row's "Arrives {date}" (renderServices) is painted from estimate() like the tile
            // and the sticky bar; without this repaint it kept the table date until the 30 s tick while the
            // two others already showed the live one
            this.renderServices();
          }
        });
      }

      startCountdown() {
        // Minute-precision tick; recomputes fully so the cutoff flip (ships
        // today → tomorrow, date + 1) can never go stale mid-session.
        this.countdownTimer = setInterval(() => {
          this.renderShipping();
          this.renderStoreTile(); // hours flip open->closed while the page idles
          this.renderSummaries(); // the mobile ship answer must flip at cutoff too
          this.renderServices(); // BOPIS: ready-time / same-day cutoff lines move with the clock
        }, 30000);
      }

      /* ---------------- Halloween clock - PE (2026-10-09) ---------------- */

      // §6: while the clock is active AND its PDP flag is on, the ship tile's
      // countdown line becomes "Order within {time} to get it {date}" with a
      // live h/min/s countdown (paexClock.bindCountdown — one shared ticker)
      // whenever the warehouse still takes today's orders, whatever the
      // processing days. Clock off / flag off: the pre-clock code path, unchanged.
      startClock() {
        // once per connection: a re-connected element gets a fresh
        // abortController (connectedCallback), so its listeners re-arm here
        const signal = this.abortController.signal;
        if (!window.paexClock || this.clockSignal === signal) return;
        this.clockSignal = signal;
        this.clock = window.paexClock;
        const rerender = () => {
          this.renderShipping();
          this.renderSummaries();
        };
        document.addEventListener('paex:clock-change', () => {
          this.refreshClockVerdict();
          rerender();
        }, { signal });
        document.addEventListener('paex:clock-cutoff', () => {
          this.refreshClockVerdict();
          rerender();
        }, { signal });
        // slot / day rollovers the clock's own signature may not carry
        document.addEventListener('paex:clock-minute', () => this.refreshClockVerdict(), { signal });
        // one-countdown rule: the occasion pill started / stopped ticking
        document.addEventListener('paex:occ-ticking', rerender, { signal });
        this.refreshClockVerdict();
        rerender();
      }

      clockActive() {
        return Boolean(this.clock && this.clock.state && this.clock.state.phase !== 'off');
      }

      clockPdp() {
        return this.clockActive() && Boolean(this.clock.config.flags && this.clock.config.flags.pdp);
      }

      // This product's Halloween verdict (its own processing days, the clock's
      // destination + rules: remote / US / unknown never get a promise).
      // Read by the occasion pill (assets/paex-occasion.js) and the sticky line
      // (assets/paex-sticky-delivery.js); 'paex:pdp-clock' tells them it moved.
      // clockEstimate is the estimate behind that verdict (table or live) —
      // the pill only pairs a table order-by date with a table-based verdict.
      // Variant / live-availability changed (Sasha 2026-10-12): the occasion
      // pill (assets/paex-occasion.js) reads onlineAvailable() + inStockStores()
      // off THIS element, so it must render AFTER this element applied the
      // change — never on the raw variantChange, whose subscriber order is the
      // block order (pill above the availability block = pill ran first and
      // showed the PREVIOUS variant's badge). Section-scoped: the quick-view
      // clone of the pill carries a rewritten data-section and must not follow.
      announceAvail() {
        document.dispatchEvent(new CustomEvent('paex:pdp-avail', {
          detail: { sectionId: this.dataset.section, variantId: this.variantId },
        }));
      }

      refreshClockVerdict() {
        const seq = (this.clockSeq = (this.clockSeq || 0) + 1);
        const publish = (verdict, estimate) => {
          const est = estimate || null;
          const sig = verdict + '|' + (est ? [est.live, est.date, est.max, est.base && est.base.arrivalKey].join(':') : '');
          if (sig === this.clockSig) return;
          this.clockSig = sig;
          this.clockVerdict = verdict;
          this.clockEstimate = est;
          this.renderShipping();
          this.renderSummaries();
          document.dispatchEvent(new CustomEvent('paex:pdp-clock', { detail: { verdict } }));
        };
        if (!this.clockActive()) {
          publish('off');
          return;
        }
        const own = Number(this.data.settings.edd.processing);
        const site = Number((this.clock.config.edd || {}).processing);
        // same processing as the site default: share the clock's live memo
        const processing = own === site ? null : own;
        const settle = (result) => {
          if (seq === this.clockSeq && this.isConnected) publish(result ? result.verdict : 'unknown', result && result.estimate);
        };
        // a failed live lookup must never leave the previous destination's
        // verdict standing: fall back to the table-only verdict, then 'unknown'
        this.clock.estimate({ processing })
          .then(settle)
          .catch(() => this.clock.estimate({ processing, live: false }).then(settle))
          .catch(() => settle(null));
      }

      // the timer is always there (Sasha 2026-10-09): it counts to the next
      // DAILY cutoff — today's noon while ahead, else tomorrow's — every day,
      // ship day or not (paexClock.bindCountdown({ daily })); the "Arrives
      // {date}" line above it still comes from the engine, so the date may
      // stay the same from one day to the next. Still needs an estimate, a
      // trusted device clock, and the occasion pill not already ticking.
      clockTimerOk(estimate) {
        if (!estimate || typeof this.clock.msToDailyCutoff !== 'function' || this.clock.msToDailyCutoff() < 1000) return false;
        if (this.clock.state.trusted === false) return false;
        return !document.querySelector('paex-occasion[data-paex-ticking]');
      }

      // binds the <b> {time} node; a visually-hidden sibling carries the
      // minute-level reading (the ticking digits are aria-hidden)
      bindClockTime(el, extra) {
        if (!el) return false;
        const sr = document.createElement('span');
        sr.className = 'visually-hidden';
        el.after(sr);
        const ok = this.clock.bindCountdown(el, {
          srEl: sr,
          daily: true,
          minutes: Boolean(extra && extra.minutes),
          onExpire: () => {
            this.renderShipping();
            this.renderSummaries();
          },
        });
        if (!ok) sr.remove();
        return ok;
      }

      // open tile: change guard on template + date, so the 30 s re-render
      // never rebuilds (and un-binds) a running countdown
      fillClockLine(line, estimate) {
        const template = this.clock.t('pdp_order_within');
        if (!template) return false;
        const date = paexEddFormatDate(estimate.arrival);
        const key = 'clk|' + template + '|' + date;
        if (line.dataset.ck === key && line.querySelector('.paex-avail__time.paex-clock-cd')) return true;
        line.dataset.ck = key;
        paexFillTemplate(line, template, { time: '', date }, { time: 'paex-avail__time' });
        return this.bindClockTime(line.querySelector('.paex-avail__time'));
      }

      /* ---------------- rendering ---------------- */

      render() {
        this.renderHeading();
        this.renderStoreTile();
        this.renderNearby();
        this.renderShipping();
        this.renderServices(); // BOPIS: before the flip / free-shipping strip, which read the selected service
        this.renderFreeShipping();
        this.renderFlip();
        this.renderSummaries();
        this.autoExpand();
      }

      // closed 2g rows carry the short tier only ("10+ at Lachenaie") —
      // the full phrase belongs to the open tile status
      tierShortText(qty) {
        const top = this.data.settings.tierTop;
        if (qty >= top) return top + '+'; // bare "10+" in the collapsed row
        return this.tierText(qty); // "Only 3 left" / "8 on the shelf" stay full
      }

      onlineTierShortText(qty) {
        // the online phrase drops "left" — "Only 2 online" keeps the arrival
        // date on one line; the store side keeps the full form (Sasha)
        if (qty <= this.tierLow && this.data.texts.onlineOnly) {
          return paexDeliveryFormat(this.data.texts.onlineOnly, { n: qty });
        }
        // the store phrase ("8 on the shelf") never reaches the online side —
        // mid tier is the bare count: "8 online" (Sasha 2026-10-05)
        const top = this.data.settings.tierTop;
        return qty >= top ? top + '+' : String(qty);
      }

      // live region armed AFTER the first content set: hydration that differs
      // from the SSR text must not announce on load
      armPoliteLive(target) {
        if (target.hasAttribute('aria-live') || target.dataset.liveArmed) return;
        target.dataset.liveArmed = '1';
        setTimeout(() => {
          target.setAttribute('aria-live', 'polite');
          target.setAttribute('aria-atomic', 'true');
        }, 0);
      }

      renderHeading() {
        const heading = this.querySelector('[data-heading]');
        if (heading) {
          const label = this.inFootprint ? heading.dataset.labelStore : heading.dataset.labelOnline;
          if (label && heading.textContent !== label) heading.textContent = label;
        }
        const eyebrow = this.querySelector('[data-ship-eyebrow]');
        if (eyebrow) {
          const label = this.inFootprint ? eyebrow.dataset.labelStore : eyebrow.dataset.labelOnline;
          if (label && eyebrow.textContent !== label) eyebrow.textContent = label;
        }
        const reassure = this.querySelector('[data-reassure]');
        if (reassure) {
          reassure.hidden = this.inFootprint;
          // "Ships in less than 24 hours" is only true while today's orders
          // still ship today (QAT audit 2026-10-09: it sat next to the
          // countdown on a Saturday / holiday) — hidden once the clock runs
          // and the warehouse no longer takes today's orders
          const ships = reassure.querySelector('[data-reassure-ships]');
          if (ships && this.clockPdp()) {
            const est = this.estimate();
            ships.hidden = Boolean(est && !est.beforeCutoff);
          }
          if (!this.inFootprint) {
            const line = reassure.querySelector('[data-reassure-freeship]');
            const target = reassure.querySelector('[data-reassure-freeship-text]');
            const settings = this.data.settings;
            const engine = settings.fsoPdp ? window.paexShippingOffer : null;
            if (line && target && engine) {
              // Free shipping offer - PE (design P3): the zone teaser rides the
              // reassurance line; remote reads "calculated at checkout"
              const text = engine.state.teaser || '';
              line.setAttribute('data-zone', engine.state.zone || 'unknown');
              if (target.textContent !== text) target.textContent = text;
              line.hidden = !text;
            }
          }
        }
      }

      renderStoreTile() {
        const tile = this.querySelector('[data-tile="store"]');
        if (!tile) return;
        const prompt = tile.querySelector('[data-store-prompt]');
        const set = tile.querySelector('[data-store-set]');
        const store =
          this.storeId && paexDeliveryHasStore(this.data.stores, this.storeId)
            ? this.data.stores[this.storeId]
            : null;

        if (!store) {
          tile.dataset.state = 'prompt';
          if (prompt) prompt.hidden = false;
          if (set) set.hidden = true;
          return;
        }
        if (prompt) prompt.hidden = true;
        if (set) set.hidden = false;

        const qty = this.variantId ? this.qtyFor(this.variantId, this.storeId) : 0;
        const tier = this.tierFor(qty);
        tile.dataset.state = tier;

        const tierTarget = tile.querySelector('[data-store-tier]');
        const text = tier === 'out'
          ? paexDeliveryFormat(this.data.texts.outAt, { store: this.displayNames[this.storeId] || '' })
          : this.tierText(qty);
        if (tierTarget && tierTarget.textContent !== text) {
          tierTarget.textContent = text;
          this.armPoliteLive(tierTarget);
        }

        const nameTarget = tile.querySelector('[data-store-name]');
        // the TILE carries the full banner name (S3/2g mocks); short names
        // stay on the chip, collapsed line, nearby pills and sheet rows
        if (nameTarget) nameTarget.textContent = store.name || this.displayNames[this.storeId] || '';

        const metaTarget = tile.querySelector('[data-store-meta]');
        if (metaTarget) this.renderStoreMeta(metaTarget, store);

        const directions = tile.querySelector('[data-directions-link]');
        if (directions) directions.href = paexDeliveryDirectionsUrl(store);
        const call = tile.querySelector('[data-call-link]');
        if (call) {
          if (store.phone) {
            call.hidden = false;
            call.href = `tel:${String(store.phone).replace(/[^+\d]/g, '')}`;
          } else {
            call.hidden = true;
          }
        }
        const page = tile.querySelector('[data-store-page]');
        const pagePill = tile.querySelector('[data-store-page-pill]');
        const safePage = paexSafeUrl(store.page);
        [page, pagePill].forEach((link) => {
          if (!link) return;
          if (safePage) {
            link.hidden = false;
            link.href = safePage;
          } else {
            link.hidden = true;
          }
        });

        this.renderRecovery(tile, tier);
      }

      // BOPIS polish (plan 6b-3): EN hour labels switch to 12-hour ("9 p.m.") through the
      // shared formatter window.paexFulfil.fmtTime (paex-fulfil.js) — the same rule as the
      // header chip's hourLabel (paex-store-chip.js). Applied ONLY when the template carries
      // the legacy "{close}:00" suffix (the EN block settings); FR templates ("{close} h",
      // Translate & Adapt) and a page without paexFulfil render byte-for-byte as today.
      // value = paexStoreTimeLabel (9 | "9:30") — or the '—' placeholder, left untouched.
      hourLabel(template, key, value) {
        const api = window.paexFulfil;
        const tpl = String(template == null ? '' : template);
        const token = '{' + key + '}:00';
        const values = {};
        if (api && typeof api.fmtTime === 'function' && value != null && tpl.indexOf(token) !== -1) {
          const parts = String(value).split(':');
          const hour = parseInt(parts[0], 10);
          const minute = parseInt(parts[1] || '0', 10) || 0;
          let label = '';
          if (isFinite(hour)) {
            try { label = api.fmtTime(hour, minute); } catch (error) { label = ''; }
          }
          if (label && label.indexOf('Translation missing') === -1) {
            values[key] = label;
            return paexDeliveryFormat(tpl.split(token).join('{' + key + '}'), values);
          }
        }
        values[key] = value;
        return paexDeliveryFormat(tpl, values);
      }

      renderStoreMeta(target, store) {
        const texts = this.data.texts;
        target.textContent = '';
        const address = store.address1 || '';
        const km = this.distanceTo(store);
        target.appendChild(document.createTextNode(address));
        if (km != null) {
          // lane A4 (visual review 8): its own span — hidden under the Pickup row, whose head states the distance
          const kmNode = document.createElement('span');
          kmNode.dataset.storeKm = '';
          kmNode.textContent = ' · ' + this.kmText(km) + ' km';
          target.appendChild(kmNode);
        }
        const info = paexStoreHoursInfo(store);
        if (!info) return;
        target.appendChild(document.createTextNode('\n'));
        const line = document.createElement('span');
        line.dataset.storeHours = ''; // hidden under the row too: the ready line already names the opening
        if (info.state === 'open') {
          paexFillTemplate(line, '{open} · {closes}', {
            open: texts.openNow,
            closes: this.hourLabel(texts.closesAt, 'close', info.closeHour),
          }, { open: 'paex-avail__open' });
        } else if (info.state === 'closes_soon') {
          line.textContent = this.hourLabel(texts.closesSoon, 'close', info.closeHour);
        } else if (info.nextOpenHour != null) {
          line.textContent = info.nextOpenIsTomorrow
            ? this.hourLabel(texts.opensTomorrow, 'open', info.nextOpenHour)
            : this.hourLabel(texts.opensAt, 'open', info.nextOpenHour);
        }
        target.appendChild(line);
      }


      // one vocabulary for the store-out fact (visual review 4): the tile's "Out at {store}" (texts.outAt with the
      // short display name) on EVERY row that states it; pickup_out only when the block setting is empty
      storeOutText(storeId) {
        const short = (storeId && this.displayNames[storeId]) || '';
        const outAt = this.data.texts.outAt ? paexDeliveryFormat(this.data.texts.outAt, { store: short }) : '';
        return outAt || this.ft('pickupOut', { store: short });
      }

      // distance for display: "231.4 km" / « 231,4 km » (visual review 44) — the storefront locale sets the decimal mark
      kmText(km) {
        if (km == null || !Number.isFinite(Number(km))) return '';
        const fr = /^fr/i.test((window.Shopify && Shopify.locale) || '');
        try {
          return Number(km).toLocaleString(fr ? 'fr-CA' : 'en-CA', { maximumFractionDigits: 1 });
        } catch (error) {
          return Number(km).toFixed(1);
        }
      }

      distanceTo(store) {
        const geo = paexDeliveryReadGeo();
        if (!geo || !Number.isFinite(geo.lat) || !Number.isFinite(geo.lng)) return null;
        if (!Number.isFinite(store.lat) || !Number.isFinite(store.lng)) return null;
        return paexDeliveryDistanceKm(geo.lat, geo.lng, store.lat, store.lng);
      }

      // Your store is out — recover the trip with the closest in-stock store
      // as a single 40px row (never a nested card), plus a "N more" link.
      renderRecovery(tile, tier) {
        const recovery = tile.querySelector('[data-recovery]');
        const more = tile.querySelector('[data-more-stores]');
        if (!recovery) return;
        const actions = tile.querySelector('[data-store-actions]');

        if (tier !== 'out') {
          recovery.hidden = true;
          if (more) more.hidden = true;
          if (actions) actions.hidden = false;
          return;
        }

        const candidates = this.inStockStores(this.storeId);
        const best = candidates[0] || null;
        if (!best) {
          recovery.hidden = true;
          if (more) more.hidden = true;
          if (actions) actions.hidden = false;
          return;
        }

        const texts = this.data.texts;
        const textTarget = recovery.querySelector('[data-recovery-text]');
        const metaTarget = recovery.querySelector('[data-recovery-meta]');
        const switchButton = recovery.querySelector('[data-recovery-switch]');
        if (textTarget) {
          paexFillTemplate(textTarget, texts.recovery, {
            tier: this.tierText(best.qty),
            store: this.displayNames[best.storeId] || '',
          }, { tier: 'paex-avail__tiergreen' });
        }
        if (metaTarget) {
          const hoursInfo = paexStoreHoursInfo(this.data.stores[best.storeId]);
          // the {close} token goes through hourLabel (12-hour EN); {km} stays a plain replace
          metaTarget.textContent = this.hourLabel(
            paexDeliveryFormat(texts.recoveryMeta, { km: this.kmText(best.distance) || '—' }),
            'close',
            hoursInfo && hoursInfo.closeHour != null ? hoursInfo.closeHour : '—'
          );
        }
        if (switchButton) switchButton.dataset.storeId = best.storeId;
        recovery.hidden = false;
        if (actions) actions.hidden = true;

        if (more) {
          const others = candidates.length - 1;
          if (others > 0) {
            more.hidden = false;
            more.textContent = paexDeliveryFormat(texts.moreStores, { n: others });
          } else {
            more.hidden = true;
          }
        }
      }

      // All OTHER stores with stock for the current variant, nearest first.
      inStockStores(excludeStoreId) {
        if (!this.variantId) return [];
        const geo = paexDeliveryReadGeo();
        const origin =
          geo && Number.isFinite(geo.lat) && Number.isFinite(geo.lng)
            ? geo
            : excludeStoreId && this.data.stores[excludeStoreId];
        const results = [];
        Object.keys(this.data.stores).forEach((storeId) => {
          if (storeId === excludeStoreId) return;
          const qty = this.qtyFor(this.variantId, storeId);
          if (qty <= 0) return;
          const store = this.data.stores[storeId];
          let distance = null;
          if (origin && Number.isFinite(origin.lat) && Number.isFinite(origin.lng) &&
              Number.isFinite(store.lat) && Number.isFinite(store.lng)) {
            distance = paexDeliveryDistanceKm(origin.lat, origin.lng, store.lat, store.lng);
          }
          results.push({ storeId, qty, distance });
        });
        results.sort((a, b) => {
          const da = a.distance == null ? Infinity : a.distance;
          const db = b.distance == null ? Infinity : b.distance;
          return da - db;
        });
        return results;
      }

      onlineQty(variantId) {
        const id = variantId || this.variantId;
        return id ? this.qtyFor(id, this.data.settings.onlineLocId) : 0;
      }

      // "sold out everywhere": Shopify's own sellability (live .js revalidation →
      // embed "_a" flag) — false means SOME online-fulfilling location can sell it
      soldOutEverywhere(variantId) {
        const id = variantId || this.variantId;
        if (!id) return true;
        const key = String(id);
        if (this.liveAvail && key in this.liveAvail) return !this.liveAvail[key];
        const variantStock = this.data.variants[key];
        if (variantStock && '_a' in variantStock) return variantStock._a !== 1;
        return false;
      }

      // M7 (2026-09-02) made the ship tile's in/out STATE Shopify's own
      // sellability (ladder: live .js revalidation → embed "_a" → qty fallback),
      // never the store_inventory mirror (a nightly snapshot that goes stale
      // intraday). BOPIS (2026-10-22, plan 6c, Track 3.1) REVERSES M7 by
      // decision: once Québec fulfils online orders, sellability turns true for
      // Québec-only variants, so "online" must mean the DC. Ladder now:
      //   sold out everywhere (live → "_a")  → false
      //   no DC entry at all (null: never synced) → sellability stands (the
      //     Validation Function's relaxed rule c — a missing entry never blocks)
      //   DC entry present → > 0
      // liveAvail is thereby demoted to the "sold out everywhere" guard; the
      // false-sold-out-intraday risk M7 fixed returns and is accepted.
      onlineAvailable(variantId) {
        const id = variantId || this.variantId;
        if (!id) return false;
        if (this.soldOutEverywhere(id)) return false;
        const variantStock = this.data.variants[String(id)];
        const loc = String(this.data.settings.onlineLocId || '');
        if (!variantStock || !loc || !(loc in variantStock) || variantStock[loc] === null) return true;
        return this.onlineQty(id) > 0;
      }

      renderShipping() {
        const tile = this.querySelector('[data-tile="ship"]');
        if (!tile) return;
        const texts = this.data.texts;
        const online = this.onlineQty();
        // M7: STATE comes from sellability; the mirror qty is count flavor only
        const availableOnline = this.onlineAvailable();
        tile.dataset.state = availableOnline ? 'in' : 'out';

        if (availableOnline && online > 0 && online <= this.tierLow) tile.dataset.shipTier = 'only';
        else delete tile.dataset.shipTier;
        const statusTarget = tile.querySelector('[data-ship-status]');
        const inBranch = tile.querySelector('[data-ship-in]');
        const outBranch = tile.querySelector('[data-ship-out]');
        if (inBranch) inBranch.hidden = !availableOnline;
        if (outBranch) outBranch.hidden = availableOnline;

        if (availableOnline) {
          const estimate = this.estimate();
          let statusText = texts.inOnline;
          if (texts.onlineTier && online > 0) {
            // 2g revision: the open tile states the online tier too; the low
            // tier reuses the short form ("Only 2 left online"). M7: when the
            // mirror says 0 but Shopify sells, the plain in-stock label shows
            // instead of a fabricated count.
            const tierTemplate = online <= this.tierLow && texts.onlineShort ? texts.onlineShort : texts.onlineTier;
            statusText = paexDeliveryFormat(tierTemplate, { tier: this.onlineTierShortText(online) });
          }
          if (statusTarget && statusTarget.textContent !== statusText) {
            statusTarget.textContent = statusText;
            this.armPoliteLive(statusTarget);
          }

          const arrives = tile.querySelector('[data-arrives]');
          const countdownLine = tile.querySelector('[data-countdown-line]');
          if (estimate) {
            if (arrives) {
              // S5: out-of-region names the destination in the date line —
              // "Arrives Fri, Oct 23 to Toronto, ON" (approved design)
              const outPlace = !this.inFootprint && texts.arrivesOut ? this.destPlace() : null;
              const text = outPlace
                ? paexDeliveryFormat(texts.arrivesOut, { date: paexEddFormatDate(estimate.arrival), place: outPlace })
                : paexDeliveryFormat(texts.arrives, { date: paexEddFormatDate(estimate.arrival) });
              if (arrives.textContent !== text) arrives.textContent = text;
            }
            // Halloween clock - PE (2026-10-09): §6 countdown (see fillClockLine)
            const clockPdp = this.clockPdp();
            if (countdownLine && clockPdp && this.clockTimerOk(estimate) && this.fillClockLine(countdownLine, estimate)) {
              // bound: the shared ticker owns the {time} text node
            } else if (countdownLine && clockPdp && estimate.shipsToday && texts.shipsToday) {
              // zero-processing product past the buffered cutoff, or the
              // occasion pill is the page's countdown: date wording, no timer
              if (countdownLine.textContent !== texts.shipsToday) countdownLine.textContent = texts.shipsToday;
            } else if (countdownLine) {
              if (!clockPdp && estimate.shipsToday && estimate.msToCutoff > 0) {
                const owPlace = this.destPlace();
                // the out countdown is static copy (method + origin); the
                // destination moved to the arrives line (approved S5)
                const owTemplate = !this.inFootprint && texts.orderWithinOut
                  ? texts.orderWithinOut
                  : texts.orderWithin;
                const owTime = paexEddFormatCountdown(estimate.msToCutoff);
                const owKey = owTemplate + '|' + owTime + '|' + (owPlace || '');
                if (countdownLine.dataset.ck !== owKey) {
                  countdownLine.dataset.ck = owKey; // change-guard per tick
                  paexFillTemplate(countdownLine, owTemplate, {
                    time: owTime,
                    place: owPlace || '',
                  }, { time: 'paex-avail__time' });
                }
              } else {
                let shipsText = texts.shipsTomorrow;
                if (estimate.dispatchDate && texts.shipsOn && typeof paexEddWarehouseNow === 'function') {
                  // "tomorrow" in the WAREHOUSE calendar, never the browser's
                  const wnow = paexEddWarehouseNow(this.data.settings.edd.tz);
                  const wtomorrow = new Date(Date.UTC(wnow.y, wnow.m - 1, wnow.d + 1, 12, 0, 0));
                  const sameDay = estimate.dispatchDate.getUTCDate() === wtomorrow.getUTCDate() &&
                    estimate.dispatchDate.getUTCMonth() === wtomorrow.getUTCMonth();
                  if (!sameDay) {
                    const day = new Intl.DateTimeFormat(
                      (window.Shopify && Shopify.locale) || 'en', { weekday: 'long', timeZone: 'UTC' }
                    ).format(estimate.dispatchDate);
                    shipsText = paexDeliveryFormat(texts.shipsOn, { day });
                  }
                }
                if (countdownLine.textContent !== shipsText) countdownLine.textContent = shipsText;
              }
            }
          } else {
            // no estimate (empty transit table): no promise, no prompt — the
            // deliver-to foot below is the affordance (Sasha 2026-07-27)
            if (arrives && arrives.textContent !== '') arrives.textContent = '';
            if (countdownLine) countdownLine.textContent = '';
          }
          // location unknown (2026-10-22): the deliver-to foot asks for the
          // postal — drop the two empty reserved lines instead of a gap
          const noDate = !estimate && this.geoUnknown;
          if (arrives) arrives.hidden = noDate;
          if (countdownLine) countdownLine.hidden = noDate;
        } else {
          if (statusTarget && statusTarget.textContent !== texts.outOnline) {
            statusTarget.textContent = texts.outOnline;
            this.armPoliteLive(statusTarget);
          }
          this.renderRestock(tile);
        }

        const deliverTo = this.querySelector('[data-deliver-to]');
        if (deliverTo) {
          // the foot answers with a destination — typed postal/ZIP, saved
          // address, geo, or the generic country name (Sasha 2026-07-27).
          // Exception (Sasha 2026-10-22): location confirmed unknown → "Enter
          // your postal code…" — "Canada" carried a date after Halloween.
          // Only the destination value carries the underline (2g/r9).
          const realPlace = this.destPlace();
          const askPostal = !realPlace && this.geoUnknown && Boolean(texts.destPrompt);
          const place = askPostal ? '\u0000ask' : realPlace || texts.destGenericPlace || '';
          if (deliverTo.dataset.pk !== place) {
            deliverTo.dataset.pk = place; // change-guard: no rebuild per tick
            if (askPostal) {
              deliverTo.textContent = '';
              const ask = document.createElement('span');
              ask.className = 'paex-avail__destplace';
              ask.textContent = texts.destPrompt;
              deliverTo.appendChild(ask);
            } else {
              paexFillTemplate(deliverTo, texts.deliverTo, { place }, { place: 'paex-avail__destplace' });
            }
          }
          const destChange = this.querySelector('[data-dest-change]');
          // mobile always shows "· Change" — the tap affordance (Sasha);
          // desktop in footprint keeps the bare underlined postal (2g)
          if (destChange) destChange.hidden = askPostal || (this.inFootprint && this.isDesktop());
        }
      }

      // Four sold-out-online messages from the restock metafields; the
      // seasonal override beats them all when the ETA misses the occasion.
      renderRestock(tile) {
        const line = tile.querySelector('[data-restock-line]');
        if (!line) return;
        const texts = this.data.texts;
        let text = '';

        const etaDate = /^\d{4}-\d{2}-\d{2}/.test(this.restockEta)
          ? new Date(`${this.restockEta.slice(0, 10)}T12:00:00Z`)
          : null;
        const arriveBy = this.dataset.occasionEnd || '';
        const etaMissesOccasion =
          this.inOccasion && etaDate && arriveBy && this.restockEta.slice(0, 10) > arriveBy;

        if (etaMissesOccasion) {
          const best = this.inStockStores(null)[0];
          text = best
            ? paexDeliveryFormat(texts.restockTooLate, {
                tier: this.tierText(best.qty),
                store: this.displayNames[best.storeId] || '',
              })
            : texts.outOnline;
        } else if (this.restock === 'incoming' && etaDate && texts.restockIncoming) {
          // bold ink date per the mock ("Back online week of Oct 20")
          paexFillTemplate(line, texts.restockIncoming, {
            date: paexEddFormatDate(etaDate, true),
          }, { date: 'paex-avail__obdate' });
          line.hidden = false;
          return;
        } else if (this.restock === 'incoming' || this.restock === 'incoming_no_eta') {
          text = texts.restockNoEta;
        } else if (this.restock === 'discontinued') {
          text = texts.restockDiscontinued;
        }
        if (line.textContent !== text) line.textContent = text;
        line.hidden = text === '';
      }

      renderFreeShipping() {
        const container = this.querySelector('[data-freeship]');
        if (!container) return;
        // BOPIS (6c): no shipping promise while the page acts for pickup / same-day
        // delivery (the selected row, or a cart already in that mode)
        if (this.fulfilService() !== 'ship') {
          if (!container.hidden) this.freeShipGated = true; // polish: remember what the gate hid
          container.hidden = true;
          return;
        }
        const gated = this.freeShipGated === true;
        this.freeShipGated = false;
        const settings = this.data.settings;
        const online = this.onlineAvailable() ? 1 : 0; // M7: bar shows whenever sellable online
        // Free shipping offer - PE (2026-10-19): the zone engine (assets/paex-shipping-offer.js)
        // owns this strip while the master switch is on. Master OFF (Sasha 2026-11-02): nothing —
        // the old cart-page toggle (enable_free_shipping_progress_bar) never drives the PDP.
        if (!settings.fsoPdp) {
          container.hidden = true;
          return;
        }
        // before the engine's first publish the server-rendered teaser stays as is
        // (review 2026-10-19: the legacy branch hid it for a frame → flash + CLS)
        if (window.paexShippingOffer) this.renderOfferStrip(container, window.paexShippingOffer.state, online);
        else if (gated) container.hidden = false; // Ship re-selected before the first publish: the teaser comes back
      }

      // Design P2 (2026-10-19): one short line + the bar under the delivery
      // tile, from the engine's state — metro "Add {amount} for free shipping*",
      // regional "{value} off shipping …", remote = plain "calculated at
      // checkout" line with NO bar and no amount (honesty rule), unknown = the
      // neutral teaser. The whole line links to the offer details.
      renderOfferStrip(container, state, online) {
        if (online <= 0 || !state || !state.line) {
          container.hidden = true;
          return;
        }
        container.hidden = false;
        container.setAttribute('data-zone', state.zone || 'unknown');
        container.setAttribute('data-state', state.kind || 'teaser');
        container.setAttribute('data-confidence', state.confidence || 'unknown');
        const bar = container.querySelector('.paex-avail__freeship-bar');
        const fill = container.querySelector('[data-freeship-fill]');
        const label = container.querySelector('[data-freeship-text]');
        if (bar) bar.hidden = state.kind === 'none';
        if (fill) fill.style.transform = `scaleX(${Number(state.progress || 0).toFixed(3)})`;
        if (!label) return;
        label.textContent = state.line;
        const printed = label.parentNode && label.parentNode.querySelector(':scope > a');
        const detailsUrl = state.details || (typeof paexSafeUrl === 'function' ? paexSafeUrl(this.data.settings.freeShipUrl) : '');
        const seeDetails = (state.texts && state.texts.seeDetails) || this.data.texts.seeDetails;
        if (!printed && detailsUrl && seeDetails) {
          label.appendChild(document.createTextNode(' · '));
          const detailsLink = document.createElement('a');
          detailsLink.className = 'link paex-avail__seedetails';
          detailsLink.href = detailsUrl;
          detailsLink.textContent = seeDetails;
          label.appendChild(detailsLink);
        }
      }

      formatMoney(cents) {
        // M5 (2026-08-03): the shop money_format ladder (webvista → Intl →
        // bare string, incl. the W3 typeof-gate lore) lives in
        // paex-cart-bridge.js — emitted right before this file, deferred;
        // call-time lookup, bare last-resort when absent.
        const b = window.PaexCartBridge;
        return b ? b.money(cents) : `$${(cents / 100).toFixed(2)}`;
      }

      // Occasion slot: in footprint = the arrive-by badge (4 outcomes, green
      // only on a real EDD); out of footprint = the order-by line. Renders
      // only inside the occasion window (the slot doesn't exist outside it).
      // The occasion badge, order-by boxes, and get-it-by line moved to the
      // standalone paex-occasion block (Sasha 2026-07-28). openPostalEditor
      // below stays public — that block bridges to it.
      // "get your date" opens the ship tile's inline postal editor
      // Sasha 2026-10-23: ONE postal editor everywhere — the shared sheet (paexFulfil.postalSheet: the clock's
      // while the clock is on, else the BOPIS sheet); the inline editors below stay only as the fallback
      postalSheet(trigger) {
        const api = this.fulfilApi();
        if (!api || typeof api.postalSheet !== 'function') return false;
        try { return api.postalSheet(trigger) === true; } catch (error) { return false; }
      }
      sheetAvailable() {
        const api = this.fulfilApi();
        try { return Boolean(api && typeof api.postalSheetAvailable === 'function' && api.postalSheetAvailable()); } catch (error) { return false; }
      }
      openPostalEditor() {
        if (this.postalSheet(this.querySelector('[data-ship-dest-open]') || this.querySelector('[data-dest-open]'))) return;
        const shipTile = this.querySelector('[data-tile="ship"]');
        if (shipTile && !shipTile.hasAttribute('data-expanded')) this.toggleTile(shipTile, false);
        const opener = this.querySelector('[data-dest-open]');
        if (opener && !opener.hidden) opener.click();
        else {
          const input = this.querySelector('[data-dest-input]');
          if (input) input.focus();
        }
      }

      todayKey() {
        if (typeof paexEddWarehouseNow === 'function') {
          const wnow = paexEddWarehouseNow(this.data.settings.edd.tz);
          return `${wnow.y}-${String(wnow.m).padStart(2, '0')}-${String(wnow.d).padStart(2, '0')}`;
        }
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      }

      // The money state: sold out online + on a shelf nearby → the primary
      // action below the tiles flips to Get directions (CTAs never in tiles).
      renderFlip() {
        const flip = this.querySelector('[data-cta-flip]');
        if (!flip) return;
        const storeQty = this.storeId && this.variantId ? this.qtyFor(this.variantId, this.storeId) : 0;
        // M7: the CTA only flips to "Get directions" when the item genuinely
        // cannot be bought online (Shopify sellability), never on a stale mirror.
        // BOPIS: not while the selector offers pickup for it — "Add for pickup"
        // is the primary action then, never Get directions beside it
        const pickupOffered = this.hasAttribute('data-services') && this.availableFor('pickup', this.variantId);
        const money = this.inFootprint && !this.onlineAvailable() && storeQty > 0 && !pickupOffered;
        this.toggleAttribute('data-money', money);
        flip.hidden = !money;
        if (!money) return;

        const store = this.data.stores[this.storeId];
        const link = flip.querySelector('[data-flip-directions]');
        const text = flip.querySelector('[data-flip-directions-text]');
        const km = this.distanceTo(store);
        if (link) link.href = paexDeliveryDirectionsUrl(store);
        if (text) {
          text.textContent = paexDeliveryFormat(this.data.texts.getDirections, {
            store: this.displayNames[this.storeId] || '',
            km: this.kmText(km) || '—',
          });
        }
        const secondary = flip.querySelector('[data-flip-secondary]');
        if (secondary) {
          const notifyButton = secondary.querySelector('[data-notify-open-secondary]');
          secondary.hidden = this.restock === 'discontinued' || !notifyButton || notifyButton.hidden;
        }
      }

      /* ---------------- mobile collapsed rows ---------------- */

      renderSummaries() {
        const texts = this.data.texts;
        const storeAnswer = this.querySelector('[data-store-answer]');
        if (storeAnswer) {
          if (this.storeId && this.data.stores[this.storeId]) {
            const qty = this.variantId ? this.qtyFor(this.variantId, this.storeId) : 0;
            const name = this.displayNames[this.storeId] || '';
            const hostRow = this.tileInRow('store') ? this.rowPlaced.store : null;
            if (hostRow) {
              // lane P5 (design selector.js, items 6 / 16): under its row the answer is the row's STATUS line —
              // "{n} in stock at {store}" / "Only {n} at {store}" (the count 600, amber at ≤ 3) / "Not in stock at
              // {store}" / the pause text + " at {store}" — the metaobject name, nothing added around it
              const answer = this.rowStoreAnswer(this.storeId, hostRow.dataset.service || 'pickup');
              const key = answer.lead + '|' + answer.name;
              if (storeAnswer.dataset.rk !== key) {
                storeAnswer.dataset.rk = key;
                paexFillTemplate(storeAnswer, this.ft('pickupIn') || '{tier} {store}', { tier: answer.lead, store: answer.name }, { tier: 'paex-avail__tiersum' });
              }
            } else if (qty > 0) {
              delete storeAnswer.dataset.rk;
              // design 1h: only the tier fragment carries colour
              paexFillTemplate(storeAnswer, texts.recovery, {
                tier: this.tierShortText(qty),
                store: name,
              }, { tier: 'paex-avail__tiersum' });
            } else {
              delete storeAnswer.dataset.rk;
              const text = paexDeliveryFormat(texts.outAt, { store: name });
              if (storeAnswer.textContent !== text) storeAnswer.textContent = text;
            }
          } else if (storeAnswer.textContent !== texts.select) {
            delete storeAnswer.dataset.rk;
            storeAnswer.textContent = texts.select;
          }
        }
        const shipAnswer = this.querySelector('[data-ship-answer]');
        const shipChannel = this.querySelector('[data-ship-channel]');
        if (shipAnswer) {
          const online = this.onlineQty();
          // M7: state from sellability; the tier fragment only renders when the
          // mirror actually has a count (otherwise the date/in-stock text alone)
          const availableOnline = this.onlineAvailable();
          let text = texts.outOnline;
          let channelMeta = '';
          let metaBit = ''; // round 2 (lane A2): the same sentence, capitalised, for the service row (no channel word)
          let clockMeta = ''; // Halloween clock - PE (2026-10-09): "order within {time}" template
          let filledShip = false;
          // lane P5 (design item 9): under the Ship row the answer is the bare status — "In stock online" (600) /
          // "Sold out online" — the count never shows and the date moved to the title line (renderShipDate)
          const shipInRow = this.tileInRow('ship');
          if (shipInRow) {
            const lead = availableOnline ? (this.ft('shipIn') || texts.inOnline || '') : (this.ft('shipOut') || texts.outOnline || '');
            if (shipAnswer.dataset.rk !== lead) {
              shipAnswer.dataset.rk = lead;
              paexFillTemplate(shipAnswer, '{lead}', { lead }, { lead: 'paex-avail__tiersum' });
            }
            filledShip = true;
          } else if (shipAnswer.dataset.rk) {
            delete shipAnswer.dataset.rk;
          }
          if (availableOnline) {
            const estimate = this.estimate();
            if (estimate) {
              const arrivalDate = paexEddFormatDate(estimate.arrival);
              if (shipInRow) {
                /* the title line carries the date (below) */
              } else if (texts.onlineShort && online > 0) {
                // C14 (PDP restyle): "10+ online · Arrives Thu, Oct 22" — the
                // count reads ink (bold), only the {date} of the setting's own
                // template is green; low stock keeps its amber tier rule
                paexFillTemplate(shipAnswer, texts.onlineShort + ' · ' + texts.arrives, {
                  tier: this.onlineTierShortText(online),
                  date: arrivalDate,
                }, { tier: 'paex-avail__tiersum', date: 'paex-avail__arrsum' });
                filledShip = true;
              } else {
                paexFillTemplate(shipAnswer, texts.arrives, { date: arrivalDate }, { date: 'paex-avail__arrsum' });
                filledShip = true;
              }
              if (this.clockPdp()) {
                // Halloween clock - PE (2026-10-09): same gate as the open tile;
                // the short locale form, its {time} bound below
                clockMeta = this.clockTimerOk(estimate) ? this.clock.t('sticky_within') : '';
              } else if (estimate.shipsToday && estimate.msToCutoff > 0) {
                metaBit = paexDeliveryFormat(texts.orderWithin, { time: paexEddFormatCountdown(estimate.msToCutoff) }).split(' · ')[0];
                channelMeta = ' · ' + metaBit.charAt(0).toLowerCase() + metaBit.slice(1);
              }
            } else if (!shipInRow && texts.onlineShort && online > 0) {
              // no estimate at all (empty transit table): the tier still shows
              paexFillTemplate(shipAnswer, texts.onlineShort, {
                tier: this.onlineTierShortText(online),
              }, { tier: 'paex-avail__tiersum' });
              filledShip = true;
            } else {
              text = texts.inOnline;
            }
          } else if (this.restock === 'incoming' && /^\d{4}-\d{2}-\d{2}/.test(this.restockEta)) {
            // 1h: the collapsed sold-out ship row carries the restock answer
            metaBit = paexDeliveryFormat(texts.restockIncoming, {
              date: paexEddFormatDate(new Date(this.restockEta.slice(0, 10) + 'T12:00:00Z'), true),
            });
            channelMeta = ' · ' + metaBit.charAt(0).toLowerCase() + metaBit.slice(1);
          }
          if (!filledShip && shipAnswer.textContent !== text) shipAnswer.textContent = text;
          if (shipChannel) {
            const base = shipChannel.dataset.base || shipChannel.textContent;
            shipChannel.dataset.base = base;
            // C13 (PDP restyle): the returns text (SSR data-returns) closes the
            // line after the countdown / restock meta — only the static tail
            // can reach the 2-line clamp, never the time-critical part
            const returns = shipChannel.dataset.returns ? ' · ' + shipChannel.dataset.returns : '';
            // round 2 (lane A2): inside the Ship row the head already says "Ship to home", so the
            // moved channel line drops the channel word and starts with the sentence itself —
            // "Order within 13 h 48 min · 10-day returns"; the tile keeps "{ch} · order within …"
            // (keyed on the tile being PLACED under its row, not on the selector being on)
            const inRows = this.tileInRow('ship');
            if (clockMeta) {
              // Sasha 2026-10-09: ONE line — "Ship to home · order within
              // 15 h 22 min" — minutes only (a width that moves once an hour
              // at most, never per second) and the countdown an unbreakable
              // unit (nowrap). Change guard: rebuilt only when the template
              // moves, so the bound countdown keeps ticking.
              const clockTemplate = inRows
                ? clockMeta + '{returns}'
                : '{ch} · ' + clockMeta.charAt(0).toLowerCase() + clockMeta.slice(1) + '{returns}';
              const clockKey = clockTemplate + '|' + base + '|' + returns;
              if (shipChannel.dataset.ck !== clockKey || !shipChannel.querySelector('.paex-avail__time.paex-clock-cd')) {
                shipChannel.dataset.ck = clockKey;
                paexFillTemplate(shipChannel, clockTemplate, { ch: base, time: '', returns }, { ch: 'paex-avail__chword', time: 'paex-avail__time' });
                if (!this.bindClockTime(shipChannel.querySelector('.paex-avail__time'), { minutes: true })) {
                  delete shipChannel.dataset.ck;
                  if (inRows) paexFillTemplate(shipChannel, '{meta}', { meta: shipChannel.dataset.returns || '' });
                  else paexFillTemplate(shipChannel, '{ch}{returns}', { ch: base, returns }, { ch: 'paex-avail__chword' });
                }
              }
            } else {
              delete shipChannel.dataset.ck;
              if (inRows) {
                paexFillTemplate(shipChannel, '{meta}', { meta: [metaBit, shipChannel.dataset.returns || ''].filter(Boolean).join(' · ') });
              } else {
                paexFillTemplate(shipChannel, '{ch}{meta}{returns}', { ch: base, meta: channelMeta, returns }, { ch: 'paex-avail__chword' });
              }
            }
          }
          // lane A3 (round 2) → lane P5: the "{place}  Modify" unit under the row's status line
          this.renderShipDest(availableOnline);
          // lane P5 (design item 9): "Ship to home · Arrives {date}" on the title line
          this.renderShipDate();
        }
        const storeChannel = this.querySelector('[data-store-channel-text]');
        const storeChange = this.querySelector('[data-store-change]');
        const hasStore = Boolean(this.storeId && this.data.stores[this.storeId]);
        if (storeChannel && hasStore) {
          const base = storeChannel.dataset.base || storeChannel.textContent;
          storeChannel.dataset.base = base;
          const store = this.data.stores[this.storeId];
          const km = this.distanceTo(store);
          const info = paexStoreHoursInfo(store);
          const bits = [base];
          if (km != null) bits.push(this.kmText(km) + ' km');
          if (info && info.state !== 'closed' && info.closeHour != null) {
            bits.push(this.hourLabel(texts.openUntil || texts.closesAt, 'close', info.closeHour));
          }
          // C13 revised (round 2): no "also {store}" bit any more — the other
          // stores live in the chevron's "Also in stock nearby" list, and the
          // sub-line closes with the Change store opener ([data-store-change])
          const meta = bits.slice(1).map((bit) => ' · ' + bit).join('');
          const inRow = this.tileInRow('store');
          if (inRow) {
            // lane P5 (design 1.2 / 1.5 / 1.9): the channel continues the row's status line — " · 4 km away" only
            // when a position is known (never for a paused row), then the opener's " · Change"; no hours, no
            // channel word (the row's head names the service)
            const paused = this.pausedToday(store, (this.rowPlaced.store && this.rowPlaced.store.dataset.service) || 'pickup');
            const away = km != null && !paused ? this.ft('pickupKm', { km: this.kmText(km) }) : '';
            paexFillTemplate(storeChannel, '{meta}', { meta: away ? ' · ' + away : '' });
          } else {
            paexFillTemplate(storeChannel, '{ch}{meta}', { ch: bits[0], meta }, { ch: 'paex-avail__chword' });
          }
          // lane P5 (CHANGES 15): in the row the store link reads "Change" / « Changer », named "Change store" /
          // « Changer de magasin » (paex.fulfil.store_change_short / store_change_aria); at home the opener keeps the
          // customizer's own label untouched — the non-participating block stays byte for byte
          const opener = storeChange && storeChange.querySelector('.paex-avail__change');
          if (opener) {
            if (inRow) {
              if (!('homeLabel' in opener.dataset)) opener.dataset.homeLabel = opener.textContent;
              const short = this.ft('storeChangeShort');
              if (short && opener.textContent !== short) opener.textContent = short;
              const aria = this.ft('storeChangeAria');
              if (aria && opener.getAttribute('aria-label') !== aria) opener.setAttribute('aria-label', aria);
            } else if ('homeLabel' in opener.dataset) {
              opener.textContent = opener.dataset.homeLabel;
              delete opener.dataset.homeLabel;
              opener.removeAttribute('aria-label');
            }
          }
        }
        if (storeChange && storeChange.hidden === hasStore) storeChange.hidden = !hasStore;
      }

      // lane A3 (round 2, design export: Ship row "In stock online · Arrives Fri, Oct 23 · G1P Modify"): the
      // destination + its change link, SSR'd in the Ship row and moved right after data-ship-answer by placeTile.
      // Shown only while the ship tile sits under its row AND the item ships (the sold-out answer has no date to
      // change). The place is the Deliver-to foot's own resolver (destPlace: typed postal pretty-printed, else the
      // geo FSA / city / region); with no destination at all the button reads the bare "Enter postal code" prompt
      // and loses the "Change postal code" aria-label (the visible prompt is its name). Change-guarded per tick.
      renderShipDest(availableOnline) {
        const dest = this.querySelector('[data-ship-dest]');
        if (!dest) return;
        const show = Boolean(availableOnline) && this.tileInRow('ship') && dest.parentNode && dest.parentNode.classList.contains('paex-avail__summary-main');
        if (dest.hidden !== !show) dest.hidden = !show;
        if (!show) return;
        const place = this.destPlace() || '';
        const placeTarget = dest.querySelector('[data-ship-dest-place]');
        if (placeTarget) {
          if (placeTarget.textContent !== place) placeTarget.textContent = place;
          if (placeTarget.hidden !== !place) placeTarget.hidden = !place;
        }
        const button = dest.querySelector('[data-ship-dest-open]');
        if (button) {
          const label = place ? (button.dataset.labelChange || '') : (button.dataset.labelPrompt || button.dataset.labelChange || '');
          if (button.textContent !== label) button.textContent = label;
          const aria = place ? (button.dataset.ariaChange || '') : '';
          if (aria) {
            if (button.getAttribute('aria-label') !== aria) button.setAttribute('aria-label', aria);
          } else if (button.hasAttribute('aria-label')) {
            button.removeAttribute('aria-label');
          }
        }
      }

      // C13 revised (PDP restyle round 2, 2026-10-05): the store row's chevron
      // expands "Also in stock nearby" — the nearest OTHER stores with stock for
      // the current variant (live embed data, nearest first), each row
      // "name · distance · quantity" + Select, then "See all {n} stores" (the
      // drawer, n = live store count). Quantity reads ink, amber at/below the
      // low tier (data-nearby-tier='only', the existing amber rule). When the
      // recovery row already shows the best store (your store is out), the
      // list starts after it — never the same store twice.
      renderNearby() {
        const box = this.querySelector('[data-nearby]');
        if (!box) return;
        const rows = box.querySelector('[data-nearby-rows]');
        const seeAll = box.querySelector('[data-see-all]');
        const texts = this.data.texts;
        const hasStore = Boolean(this.storeId && this.data.stores[this.storeId]);
        if (!rows || !hasStore || !this.inFootprint) {
          box.hidden = true;
          return;
        }
        let list = this.inStockStores(this.storeId);
        const recovery = this.querySelector('[data-recovery]');
        if (recovery && !recovery.hidden && list.length) list = list.slice(1);
        list = list.slice(0, 3); // design: the nearest three, See all for the rest
        const key = list.map((row) => row.storeId + ':' + row.qty + ':' + (row.distance == null ? '' : row.distance.toFixed(1))).join('|');
        if (rows.dataset.key !== key) {
          rows.dataset.key = key;
          rows.textContent = '';
          list.forEach((row) => {
            const name = this.displayNames[row.storeId] || '';
            const li = document.createElement('li');
            const line = document.createElement('span');
            const nameNode = document.createElement('b');
            nameNode.textContent = name;
            line.appendChild(nameNode);
            if (row.distance != null) line.appendChild(document.createTextNode(' · ' + this.kmText(row.distance) + ' km'));
            line.appendChild(document.createTextNode(' · '));
            const qty = document.createElement('span');
            qty.dataset.nearbyTier = this.tierFor(row.qty);
            qty.textContent = this.tierText(row.qty);
            line.appendChild(qty);
            const select = document.createElement('button');
            select.type = 'button';
            select.className = 'paex-avail__switch link';
            select.dataset.storeId = row.storeId;
            select.textContent = texts.nearbySelect || '';
            const hidden = document.createElement('span');
            hidden.className = 'visually-hidden';
            hidden.textContent = ' ' + name;
            select.appendChild(hidden);
            li.appendChild(line);
            li.appendChild(select);
            rows.appendChild(li);
          });
        }
        const title = box.querySelector('[data-nearby-title]');
        if (title) title.hidden = list.length === 0;
        rows.hidden = list.length === 0;
        if (seeAll) {
          const total = Object.keys(this.data.stores).length;
          const label = paexDeliveryFormat(texts.nearbySeeAll || '', { n: total });
          if (seeAll.textContent !== label) seeAll.textContent = label;
          seeAll.hidden = !label;
        }
        // the recovery's own "{n} more stores" link would repeat See all
        const more = this.querySelector('[data-more-stores]');
        if (more && seeAll && !seeAll.hidden) more.hidden = true;
        box.hidden = false;
      }

      /* ---------------- BOPIS (2026-10-22): "Buy online" selector, button override, sticky contract ----------------
         Plan 6b/6c + seams §3 (contracts). Everything below feature-detects window.paexFulfil (assets/paex-fulfil.js,
         the mode lane: mode / service / locationId / participates / pausedToday / readyLine / localLine / fmtTime /
         select) and the paex.fulfil.* strings in texts.fulfil. When either is absent the block renders exactly as
         before: no selector, no button override beyond the Track 3.1 DC-out gate (which is Shopify-independent). */

      fulfilApi() {
        const api = window.paexFulfil;
        return api && typeof api === 'object' ? api : null;
      }

      // texts.fulfil.* (paex.fulfil.* strings, seams §3.5) — '' when the key is absent. The object is the
      // snake_case paex-fulfil-strings table plus a few camelCase extras (soldOut, tomorrow…); a camelCase
      // key that is not present is looked up by its snake_case name (serviceShip → service_ship).
      ft(key, values) {
        const texts = this.data.texts && this.data.texts.fulfil;
        let template = texts ? texts[key] : null;
        if (texts && typeof template !== 'string') template = texts[key.replace(/[A-Z]/g, (c) => '_' + c.toLowerCase())];
        if (typeof template !== 'string' || /^Translation missing/i.test(template)) return '';
        return values ? paexDeliveryFormat(template, values) : template;
      }

      ftDay(weekday) {
        const texts = this.data.texts && this.data.texts.fulfil;
        const days = texts && Array.isArray(texts.days) ? texts.days : [];
        const label = days[weekday];
        return typeof label === 'string' && !/^Translation missing/i.test(label) ? label : '';
      }

      // the shared 12-hour EN / 24-hour FR formatter (decision 6b-3) lives in paex-fulfil.js
      // (paexFulfil.fmtTime); the local fallback renders the same locale templates
      fmtTime(minutes) {
        const api = this.fulfilApi();
        const h24 = Math.floor(minutes / 60) % 24;
        const m = minutes % 60;
        if (api && typeof api.fmtTime === 'function') {
          try {
            const out = api.fmtTime(h24, m);
            if (out) return String(out);
          } catch (error) { /* local below */ }
        }
        if (h24 === 12 && m === 0 && this.ft('noon')) return this.ft('noon');
        if (h24 === 0 && m === 0 && this.ft('midnight')) return this.ft('midnight');
        const fr = /^fr/i.test((window.Shopify && Shopify.locale) || '');
        const mm = m ? (fr ? ' ' : ':') + (m < 10 ? '0' : '') + m : '';
        const template = this.ft(h24 < 12 ? 'fmtAm' : 'fmtPm');
        if (!template) return typeof paexStoreTimeLabel === 'function' ? String(paexStoreTimeLabel(minutes)) : String(h24);
        return paexDeliveryFormat(template, { h: fr ? h24 : (h24 % 12 || 12), m: mm });
      }

      // paexStoreTimeLabel output (number = whole hours, "H:MM") back to minutes
      labelMinutes(label) {
        if (label == null) return null;
        if (typeof label === 'number') return label * 60;
        return typeof paexStoreParseMinutes === 'function' ? paexStoreParseMinutes(String(label)) : null;
      }

      // the metaobject cutoff as typed by the team: "14:00" / "14h" / "14h30" / "14"
      cutoffMinutes(value) {
        const match = String(value == null ? '' : value).match(/^\s*(\d{1,2})\s*(?:[:h]\s*(\d{2})?)?\s*$/i);
        if (!match) return null;
        return parseInt(match[1], 10) * 60 + (match[2] ? parseInt(match[2], 10) : 0);
      }

      storeTz() {
        return (this.data.settings.edd && this.data.settings.edd.tz) || undefined;
      }

      // "now" in settings.paex_dispatch_tz (seams §3.2 pausedToday) — weekday, minutes, dateKey
      storeNow() {
        return typeof paexStoreNowParts === 'function' ? paexStoreNowParts(new Date(), this.storeTz()) : null;
      }

      storeDateKey() {
        const parts = this.storeNow();
        return (parts && parts.dateKey) || this.todayKey();
      }

      // paused = the service toggle OR today (store time) in the blackout list
      pausedToday(store, service) {
        if (!store) return false;
        const api = this.fulfilApi();
        if (api && typeof api.pausedToday === 'function') {
          try { return Boolean(api.pausedToday(store, service)); } catch (error) { /* local below */ }
        }
        if (service === 'pickup' ? store.pickupPaused === true : store.localPaused === true) return true;
        const dates = Array.isArray(store.blackoutDates) ? store.blackoutDates : [];
        return dates.indexOf(this.storeDateKey()) !== -1;
      }

      // "back {date}": the first non-blackout day after today's blackout run (a toggle pause has no end date)
      pausedBackText(store) {
        const dates = Array.isArray(store && store.blackoutDates) ? store.blackoutDates : [];
        const today = this.storeDateKey();
        if (!today || dates.indexOf(today) === -1 || typeof paexEddFormatDate !== 'function') return '';
        const cursor = new Date(`${today}T12:00:00Z`);
        for (let step = 0; step < 60; step += 1) {
          cursor.setUTCDate(cursor.getUTCDate() + 1);
          const key = cursor.toISOString().slice(0, 10);
          if (dates.indexOf(key) === -1) return this.ft('pausedBack', { date: paexEddFormatDate(cursor, true) });
        }
        return '';
      }

      // lane P5 (design 1.9 / 1.9b, CHANGES 18): the short pause sentence — paexFulfil.pauseText(store, svc) when the
      // helper ships one ("Not available right now" / "Not available Oct 30–31"), else paused_now
      pauseText(store, service) {
        const api = this.fulfilApi();
        if (api && typeof api.pauseText === 'function') {
          try {
            const text = api.pauseText(store, service);
            if (text) return String(text);
          } catch (error) { /* local below */ }
        }
        return this.ft('pausedNow') || this.ft(service === 'pickup' ? 'pausedPickup' : 'pausedLocal', { store: (store && store.name) || '' });
      }

      // the blackout range alone ("Oct 30–31") for the panel's paused_both_dates — the helper's blackoutRange when
      // present, else the range read back out of its pauseText through the paused_dates template; '' = no range
      pauseRange(store, service) {
        const api = this.fulfilApi();
        if (!api) return '';
        try {
          if (typeof api.pauseReason === 'function' && api.pauseReason(store, service) !== 'blackout') return '';
          if (typeof api.blackoutRange === 'function') {
            const range = api.blackoutRange(store, service);
            if (range) return String(range);
          }
          const template = this.ft('pausedDates');
          const text = typeof api.pauseText === 'function' ? String(api.pauseText(store, service) || '') : '';
          const cut = template.indexOf('{range}');
          if (!text || cut === -1) return '';
          const head = template.slice(0, cut);
          const tail = template.slice(cut + 7);
          if (text.indexOf(head) === 0 && (!tail || text.slice(-tail.length) === tail)) return text.slice(head.length, text.length - tail.length).trim();
        } catch (error) { /* no range */ }
        return '';
      }

      // the selector's own low-stock cut-off (design item 6: "Only {n}" at 3 or fewer) — settings.tierLowSelector of
      // the texts island (snippets/paex-fulfil-texts.liquid), never the tiles' paex_tier_low
      selectorTierLow() {
        if (this.selectorLow != null) return this.selectorLow;
        let low = null;
        const api = this.fulfilApi();
        if (api && api.settings && api.settings.tierLowSelector != null) low = Number(api.settings.tierLowSelector);
        if (!Number.isFinite(low) || low <= 0) {
          const island = document.querySelector('script[data-paex-fulfil-texts]');
          try {
            const parsed = island ? JSON.parse(island.textContent) : null;
            low = parsed && parsed.settings ? Number(parsed.settings.tierLowSelector) : null;
          } catch (error) { low = null; }
        }
        this.selectorLow = Number.isFinite(low) && low > 0 ? low : 3;
        return this.selectorLow;
      }

      // the row's state vocabulary: out | only (≤ tierLowSelector) | in — exact counts, never the capped "10+"
      rowTierFor(qty) {
        if (qty <= 0) return 'out';
        return qty <= this.selectorTierLow() ? 'only' : 'in';
      }

      // the Pickup / Same-day row's status lead + store (design selector.js, items 6 / 16): paused → the pause text,
      // stock → "{n} in stock" / "Only {n}", out → "Not in stock"; composed by pickup_in "{tier} at {store}" with the
      // metaobject name as is
      rowStoreAnswer(storeId, service) {
        const store = this.data.stores[storeId] || {};
        const name = store.name || this.displayNames[storeId] || '';
        const qty = this.variantId ? this.qtyFor(this.variantId, storeId) : 0;
        if (this.pausedToday(store, service)) return { lead: this.pauseText(store, service), name, state: 'paused' };
        if (qty <= 0) return { lead: this.ft('tierOutLead'), name, state: 'out' };
        const state = this.rowTierFor(qty);
        return { lead: this.ft(state === 'only' ? 'tierOnlyN' : 'tierInN', { n: qty }), name, state };
      }

      // "Ship to home · Arrives Fri, Oct 23" (design item 9): the date on the Ship row's title line while the item
      // ships and an estimate exists; hidden otherwise (sold out online shows no date)
      renderShipDate() {
        const box = this.querySelector('[data-services-box]');
        const target = box && box.querySelector('[data-service="ship"] [data-service-date]');
        if (!target) return;
        let text = '';
        if (this.hasAttribute('data-services') && this.onlineAvailable() && typeof paexEddFormatDate === 'function') {
          const estimate = this.estimate();
          if (estimate) text = this.ft('shipArrives', { date: paexEddFormatDate(estimate.arrival) });
        }
        // the label row already spaces its items (flex gap): the date carries only the trailing space of the separator
        const value = text ? '· ' + text : '';
        if (target.textContent !== value) target.textContent = value;
        if (target.hidden !== !value) target.hidden = !value;
      }

      // the Pickup title-line ready sentence (the ship date's twin): "· Usually ready in 2 hours" / "· Ready tomorrow
      // around noon" from readyDetail() (paexFulfil.readyLine().text), hidden when the row cannot be bought
      renderPickupReady(store, show) {
        const box = this.querySelector('[data-services-box]');
        const target = box && box.querySelector('[data-service="pickup"] [data-service-ready]');
        if (!target) return;
        const text = show ? this.readyDetail(store) : '';
        const value = text ? '· ' + text : '';
        if (target.textContent !== value) target.textContent = value;
        if (target.hidden !== !value) target.hidden = !value;
      }

      prepText(store) {
        const n = Number(store && store.prepHours);
        if (!Number.isFinite(n) || n <= 0) return '';
        return n === 1 ? this.ft('prepHoursOne') : this.ft('prepHours', { n });
      }

      holdText(store) {
        const n = Number(store && store.holdDays);
        if (!Number.isFinite(n) || n <= 0) return '';
        return n === 1 ? this.ft('holdDaysOne') : this.ft('holdDays', { n });
      }

      // readyLine (seams §3.2): "now + prep before closing, else next opening + prep" in store time with the
      // store's holiday hours (never "ready at opening") — paexFulfil.readyLine when present, same rule locally.
      // Returns { when, short, closed } (null = no hours at all):
      //   when   the computed line — ready_today "Today around {time}" / ready_when "{day} around {time} ({prep}
      //          after we open at {open})" — '' when the hours give no answer; NEVER the base sentence (the
      //          helper's .text IS ready_base in its today branch, which is why the row used to print it twice)
      //   closed false only when the order is ready today during store hours (now + prep before closing)
      //   short  the bar form: the helper's `inline` ("ready in 2 h" / "ready tomorrow around noon"); without the
      //          helper, the computed line minus its trailing parenthetical (no string of its own — lane D3
      //          removed ready_short / ready_sub / ready_usually)
      readyWhen(store) {
        // lane P5: the local shape's `short` is the helper's inline form — ready_inline_open "ready in {prep}" (the
        // abbreviated prep_short) / ready_inline_closed "ready {day} around {time}" — never the capitalised `when`
        const shape = (when, closed, inline) => {
          const text = when || '';
          return { when: text, closed: Boolean(closed), short: inline || text.replace(/\s*\([^()]*\)\s*$/, '') };
        };
        const api = this.fulfilApi();
        if (api && typeof api.readyLine === 'function') {
          try {
            const line = api.readyLine(store, new Date());
            if (line && line.text) {
              const shaped = shape(line.unknown ? '' : line.when, line.closed !== false);
              // lane A4 (D3 contract): the bar's form is the helper's lowercase verb-first `inline` ("ready in 2 hours" /
              // "ready tomorrow around noon") when it ships one; the local shape stands for an older helper
              if (line.inline) shaped.short = String(line.inline);
              return shaped;
            }
          } catch (error) { /* local below */ }
        }
        if (!store || !store.hours || typeof paexStoreHoursInfo !== 'function') return null;
        const parts = this.storeNow();
        const info = paexStoreHoursInfo(store, new Date(), { timeZone: this.storeTz(), holidays: store.holidays });
        if (!parts || !info) return null;
        const prepMin = Math.max(0, Number(store.prepHours) || 0) * 60;
        const closeMin = this.labelMinutes(info.closeHour);
        const prepN = Number(store.prepHours) || 0;
        const prepShort = prepN === 1 ? this.ft('prepShortOne') : this.ft('prepShort', { n: prepN });
        if ((info.state === 'open' || info.state === 'closes_soon') && closeMin != null && parts.minutes + prepMin <= closeMin) {
          return shape(this.ft('readyToday', { time: this.fmtTime(parts.minutes + prepMin) }), false, this.ft('readyInlineOpen', { prep: prepShort || this.prepText(store) }));
        }
        const openMin = this.labelMinutes(info.nextOpenHour);
        if (openMin == null) return null;
        if (info.nextOpenIsToday) {
          const time = this.fmtTime(openMin + prepMin);
          return shape(this.ft('readyToday', { time }), true, this.ft('readyInlineClosed', { day: this.ft('today'), time }));
        }
        const dayWord = info.nextOpenIsTomorrow ? this.ft('tomorrow') : this.ftDay(info.nextOpenWeekday);
        const day = dayWord ? dayWord.charAt(0).toUpperCase() + dayWord.slice(1) : '';
        const time = this.fmtTime(openMin + prepMin);
        return shape(
          this.ft('readyWhen', { day, time, prep: this.prepText(store), open: this.fmtTime(openMin) }),
          true,
          this.ft('readyInlineClosed', { day: dayWord || '', time })
        );
      }

      // the line under the selected Pickup row = exactly ONE sentence, paexFulfil.readyLine(store, now).text
      // (lane D3: "Usually ready in 2 hours · held 3 days" / "Ready tomorrow around noon (2 hours after we open at
      // 10 a.m.) · held 3 days") — nothing prepended or appended (visual review 2 / 41: the row printed `when` AND
      // the base sentence). Without the helper the local rule renders the same shape from the strings.
      readyDetail(store) {
        const api = this.fulfilApi();
        if (api && typeof api.readyLine === 'function') {
          try {
            const line = api.readyLine(store, new Date());
            if (line && line.text) return String(line.text);
          } catch (error) { /* local below */ }
        }
        // lane P5 (CHANGES 12 / 19): no hold days and no parenthetical on the site — "Usually ready in 2 hours" /
        // "Ready tomorrow around noon" (the helper's text already reads so; the local shape trims the same)
        const when = this.readyWhen(store);
        if (when && when.closed && when.when) {
          const short = when.short || when.when;
          return short.charAt(0).toUpperCase() + short.slice(1); // the helper's .text form: "Ready tomorrow around noon"
        }
        return this.ft('readyBase', { prep: this.prepText(store), hold: '' }).replace(/\s*·\s*$/, '');
      }

      // the postal the same-day zone test runs on — the SAME sources, in the same order, as the Ship row's "· J7V
      // Change" (typed destination / edd_postal → address on file → geo postal); an FSA alone answers (the zone rules
      // are prefix rules), so a shopper the Ship row already places at J7V is never asked again (visual review 3)
      localPostal() {
        const dest = typeof paexDestRead === 'function' ? paexDestRead() : null;
        const geo = paexDeliveryReadGeo();
        const candidates = dest ? [dest.postal, dest.place] : [this.dataset.customerZip, geo && geo.postal];
        for (const value of candidates) {
          const clean = String(value || '').replace(/\s+/g, '').toUpperCase();
          if (/^[A-Z]\d[A-Z](\d[A-Z]\d)?$/.test(clean)) return clean.length > 3 ? `${clean.slice(0, 3)} ${clean.slice(3)}` : clean;
        }
        return null;
      }

      // localLine (seams §3.2): FSA prefixes of the store metaobject + its cutoff in store time —
      // paexFulfil.localLine when present, the same rule locally. state: prompt | ok | outside
      // Returns { state, text, postal, cutoff, today, line }: text = the helper's sentence ("Order before 6 p.m. for
      // today" / "Delivered tomorrow around noon"), cutoff = its formatted cutoff, today = the order can go today
      localInfo(store) {
        const name = store.name || '';
        const postal = this.localPostal();
        if (!postal) return { state: 'prompt', text: this.ft('localPrompt'), postal: null, cutoff: null, today: false };
        const api = this.fulfilApi();
        if (api && typeof api.localLine === 'function') {
          try {
            const line = api.localLine(store, postal, new Date());
            if (line && typeof line === 'object') {
              const todayKey = this.storeDateKey();
              return {
                state: line.ok ? 'ok' : 'outside',
                text: line.text || (line.ok ? '' : this.ft('localOutside', { postal, store: name })),
                postal,
                cutoff: line.cutoff || null,
                today: Boolean(line.ok && line.dayKey && todayKey && line.dayKey === todayKey),
                line,
              };
            }
          } catch (error) { /* local below */ }
        }
        const prefixes = String(store.localPrefixes || '').toUpperCase().split(/[\s,;|]+/)
          .map((prefix) => prefix.replace(/[^A-Z0-9]/g, '')).filter(Boolean);
        const clean = postal.replace(/\s+/g, '');
        if (!prefixes.some((prefix) => clean.indexOf(prefix) === 0)) {
          return { state: 'outside', text: this.ft('localOutside', { postal, store: name }), postal, cutoff: null, today: false };
        }
        const parts = this.storeNow();
        const info = store.hours && typeof paexStoreHoursInfo === 'function'
          ? paexStoreHoursInfo(store, new Date(), { timeZone: this.storeTz(), holidays: store.holidays })
          : null;
        // lane P5 (checklist 4b): the cutoff = closing − prep − the courier buffer, capped by a typed cutoff when the
        // metaobject carries one (the helper owns the rule; this is the same arithmetic for the page without it)
        const closeMin = info && (info.state === 'open' || info.state === 'closes_soon') ? this.labelMinutes(info.closeHour) : null;
        const prepMin = Math.max(0, Number(store.prepHours) || 0) * 60;
        const bufferMin = Math.max(0, Number(store.courierBufferHours) || 0) * 60;
        const computed = closeMin != null ? closeMin - prepMin - bufferMin : null;
        const typed = this.cutoffMinutes(store.localCutoff);
        let cutoffMin = computed;
        if (typed != null) cutoffMin = computed != null ? Math.min(typed, computed) : typed;
        const openToday = Boolean(info && (info.state === 'open' || info.state === 'closes_soon' || info.nextOpenIsToday));
        if (cutoffMin != null && parts && parts.minutes < cutoffMin && openToday) {
          const cutoff = this.fmtTime(cutoffMin);
          return { state: 'ok', text: this.ft('localToday', { cutoff }), postal, cutoff, today: true };
        }
        let day = this.ft('tomorrow');
        if (info && info.nextOpenWeekday != null && !info.nextOpenIsToday && !info.nextOpenIsTomorrow) day = this.ftDay(info.nextOpenWeekday) || day;
        return { state: 'ok', text: this.ft('localNext', { day }), postal, cutoff: null, today: false };
      }

      // the store the service rows name: the cart's store once a mode exists (6c), else the resolved store
      serviceStoreId() {
        const api = this.fulfilApi();
        try {
          const mode = api && typeof api.mode === 'function' ? api.mode() : null;
          const loc = mode && typeof api.locationId === 'function' ? api.locationId(mode) : null;
          if (loc && paexDeliveryHasStore(this.data.stores, String(loc))) return String(loc);
        } catch (error) { /* resolved store below */ }
        return this.storeId;
      }

      // 'ship' | 'pickup' | 'local' — the service the page acts for (seams §3.3: carrier → selector → ship)
      fulfilService() {
        if (this.hasAttribute('data-services')) return this.getAttribute('data-selected-service') || 'ship';
        const api = this.fulfilApi();
        try {
          const mode = api && typeof api.mode === 'function' ? api.mode() : null;
          const service = mode && typeof api.service === 'function' ? api.service(mode) : null;
          if (service === 'pickup' || service === 'local') return service;
        } catch (error) { /* ship */ }
        return 'ship';
      }

      // inside the resolved store's own pickup_radius_km (plan §3, decision 6b): a chosen store counts as
      // inside; a typed postal is measured from its FSA centroid, IP geo from its position; no position and
      // no chosen store = outside. paexFulfil.participates().inRadius answers when this element cannot.
      inPickupRadius(storeId, part) {
        const store = this.data.stores[storeId];
        if (!store) return false;
        if (this.storeIsExplicit) return true;
        const radius = Number(store.radiusKm) || Number(part && part.radiusKm) || 0;
        if (radius <= 0) return false;
        let km = this.storeKm;
        if (km == null) km = this.distanceTo(store);
        if (km != null) return km <= radius;
        if (part && typeof part.inRadius === 'boolean') return part.inRadius;
        return false;
      }

      // null = today's informational block (no selector, nothing written); else the participation record
      servicesDecision() {
        const api = this.fulfilApi();
        if (!api || !this.pickupEnabled || !this.ft('legend')) return null;
        if (!this.storeId || !paexDeliveryHasStore(this.data.stores, this.storeId)) return null;
        const store = this.data.stores[this.storeId];
        if (store.pickup !== true && store.local !== true) return null;
        let part = null;
        if (typeof api.participates === 'function') {
          try { part = api.participates(this.storeId) || null; } catch (error) { part = null; }
        }
        // lane P5 (design 0.3): the selector shows whenever participates().gated says so — the mode lane's one
        // gate (radius, and the cart already in pickup / same-day); this element's own radius test stands in
        // for an older helper without the flag. Nothing changes for a non-participating store (gated false).
        const gated = part && part.gated === true;
        if (!gated && !this.inPickupRadius(this.storeId, part)) return null;
        return part || { pickup: store.pickup === true, local: store.local === true, store };
      }

      // units for a service: the DC entry for ship, the store entry (capped at tierTop) for pickup / same-day
      availableFor(service, variantId) {
        if (!variantId) return false;
        if (service === 'ship') return this.onlineAvailable(variantId);
        const storeId = this.serviceStoreId();
        const store = storeId && this.data.stores[storeId];
        if (!store) return false;
        if (service === 'pickup' && store.pickup !== true) return false;
        if (service === 'local' && store.local !== true) return false;
        if (this.pausedToday(store, service)) return false;
        if (service === 'local' && this.localInfo(store).state !== 'ok') return false;
        return this.qtyFor(variantId, storeId) > 0;
      }

      // quantity cap = the store entry (the embed caps it at tierTop: a "10+" store answers 10)
      capFor(service, variantId) {
        if (service !== 'pickup' && service !== 'local') return null;
        const storeId = this.serviceStoreId();
        return storeId ? this.qtyFor(variantId, storeId) : null;
      }

      // The selector: rows ship / pickup / local, rendered only when the resolved store participates and the
      // shopper is inside its radius (rule 6: a cart mode never resurrects it outside); rows for a service the
      // store has not enabled are removed, paused rows grey with the note, the chosen row stays selected when a
      // size switch makes it unavailable (the button carries the reason).
      renderServices() {
        // BOPIS polish: the free-shipping strip ([data-freeship]) follows the service the page acts
        // for — the selected row included, not only the cart mode — on every pass that relabels the
        // button (radio change, paex:fulfil-change, typed postal, the clock)
        const serviceBefore = this.fulfilService();
        this.renderServiceRows();
        if (this.fulfilService() !== serviceBefore) this.renderFreeShipping();
      }

      renderServiceRows() {
        const box = this.querySelector('[data-services-box]');
        if (!box) {
          this.applyButton();
          return;
        }
        const part = this.servicesDecision();
        if (!part) {
          if (!box.hidden) box.hidden = true;
          this.removeAttribute('data-services');
          this.removeAttribute('data-selected-service');
          this.syncRowLayout(null); // lane A2: the tiles go home — today's informational block, as on live
          this.renderShipDate();
          this.renderNudge();
          this.applyButton();
          this.stampPills();
          return;
        }
        const storeId = this.serviceStoreId();
        const store = this.data.stores[storeId];
        // the store's display name = the metaobject name as is (Sasha renames it in Shopify) — nothing added around it
        const name = store.name || this.displayNames[storeId] || '';
        const legend = box.querySelector('[data-services-legend]');
        if (legend && legend.textContent !== this.ft('legend')) legend.textContent = this.ft('legend');

        const shipOk = this.onlineAvailable();
        const storeQty = this.variantId ? this.qtyFor(this.variantId, storeId) : 0;
        const pickupShown = store.pickup === true;
        const localShown = store.local === true;
        const pickupPaused = pickupShown && this.pausedToday(store, 'pickup');
        const localPaused = localShown && this.pausedToday(store, 'local');
        const pickupOk = pickupShown && !pickupPaused && storeQty > 0;
        const local = localShown ? this.localInfo(store) : null;
        const localOk = Boolean(local) && !localPaused && local.state === 'ok' && storeQty > 0;
        const shown = { ship: true, pickup: pickupShown, local: localShown };

        // selection (seams §3.3): cart mode → the shopper's own pick on this page → by stock (ship, else pickup)
        let selected = null;
        const api = this.fulfilApi();
        try {
          const mode = typeof api.mode === 'function' ? api.mode() : null;
          const modeService = mode && typeof api.service === 'function' ? api.service(mode) : null;
          if (modeService && shown[modeService]) selected = modeService;
        } catch (error) { selected = null; }
        if (this.serviceChosen && this.selectedService && shown[this.selectedService]) selected = this.selectedService;
        if (!selected) selected = shipOk ? 'ship' : (pickupOk ? 'pickup' : 'ship');
        this.selectedService = selected;

        const pauseNote = String(store.pauseNote || '').trim();
        // lane A2 (round 2): the tiles' live summaries move INTO the rows (syncRowLayout) — the Ship row carries
        // data-ship-answer ("In stock online", lane P5) + data-ship-channel (the clock-bound "Order within 13 h 48
        // min" meta line), the Pickup row data-store-answer ("7 in stock at {store}" / the pause text, painted by
        // renderSummaries → rowStoreAnswer) + data-store-channel (" · 4 km away · Change"). Their own status line is
        // therefore empty; the local row (no tile) keeps its full status.
        // lane P5 (design selector.js): a status = { lead, rest } — the lead phrase ("Eligible", "In stock online",
        // the count, the pause text) is 600 (.paex-avail__tiersum), the rest ink 500; nothing is coloured.
        const liveShip = Boolean(this.liveSlotFor('ship')) && Boolean(this.querySelector('[data-tile="ship"]'));
        const livePickup = pickupShown && Boolean(this.liveSlotFor('pickup')) && Boolean(this.querySelector('[data-tile="store"]'));
        const atStore = (lead) => ({ t: this.ft('pickupIn') || '{tier} {store}', v: { tier: lead, store: name } });
        const storeAnswer = (service) => {
          const answer = this.rowStoreAnswer(storeId, service);
          return atStore(answer.lead);
        };
        let localStatus = null;
        let localState = 'prompt';
        if (localShown) {
          if (localPaused) { localState = 'paused'; localStatus = { t: '{tier}', v: { tier: this.pauseText(store, 'local') } }; }
          else if (storeQty <= 0) { localState = 'out'; localStatus = storeAnswer('local'); }
          else if (!local || local.state === 'prompt') { localState = 'prompt'; localStatus = { t: '{text}', v: { text: this.ft('localPrompt') } }; }
          else if (local.state === 'outside') { localState = 'out'; localStatus = { t: '{text}', v: { text: local.text || this.ft('localOutside', { postal: local.postal, store: name }) } }; }
          else { localState = 'in'; localStatus = { t: this.ft('localEligible') || '{lead}', v: { lead: this.ft('localEligibleLead'), store: name } }; }
        }
        const rows = {
          ship: {
            state: shipOk ? 'in' : 'out',
            label: this.ft('serviceShip'),
            status: liveShip ? null : { t: '{tier}', v: { tier: shipOk ? this.ft('shipIn') : this.ft('shipOut') } },
            detail: '',
            ok: shipOk,
          },
          pickup: {
            state: pickupPaused ? 'paused' : (storeQty > 0 ? this.rowTierFor(storeQty) : 'out'),
            label: this.ft('servicePickup'),
            status: livePickup ? null : storeAnswer('pickup'),
            detail: '',
            ok: pickupOk,
          },
          // design findings 1 / 44: the stock test comes BEFORE the postal prompt — paused → not in stock at
          // the store (grey, not selectable, no postal invitation) → prompt → outside the zone → eligible
          local: {
            state: localState,
            label: this.ft('serviceLocal'),
            status: localStatus,
            detail: '',
            // the prompt state is selectable only while the store has the item: choosing the row is how the
            // postal editor opens
            ok: localOk || Boolean(local && local.state === 'prompt' && !localPaused && storeQty > 0),
          },
        };
        // the detail line of the SELECTED row only (design 1.2 / 1.7): the ready promise (readyLine().text) under
        // Pickup, the cutoff sentence (localLine().text, "Order before 6 p.m. for today") under Same-day — never a
        // fee (CHANGES 14), never a date under Ship (the title line carries it)
        // Sasha 2026-10-23: the ready promise rides the Pickup TITLE line ("Pick up in store · Usually ready in 2 hours",
        // like the ship date) whenever the store has the item and the service runs — no detail line under the row
        this.renderPickupReady(store, Boolean(pickupOk && !pickupPaused && storeQty > 0));
        if (selected === 'local' && localOk) rows.local.detail = local.text || '';

        box.querySelectorAll('[data-service]').forEach((row) => {
          const service = row.dataset.service;
          const spec = rows[service];
          if (!spec) return;
          const isShown = Boolean(shown[service]);
          if (row.hidden !== !isShown) row.hidden = !isShown;
          if (!isShown) return;
          const isSelected = service === selected;
          if (row.dataset.state !== spec.state) row.dataset.state = spec.state;
          row.toggleAttribute('data-selected', isSelected);
          const radio = row.querySelector('[data-service-radio]');
          if (radio) {
            if (radio.checked !== isSelected) radio.checked = isSelected;
            if (spec.ok) radio.removeAttribute('aria-disabled');
            else radio.setAttribute('aria-disabled', 'true');
          }
          const setText = (selector, value) => {
            const target = row.querySelector(selector);
            if (target && target.textContent !== value) target.textContent = value;
            return target;
          };
          setText('[data-service-label]', spec.label);
          setText('[data-service-caret-name]', this.ft('rowDetails')); // the chevron: "{service} Details"
          const status = row.querySelector('[data-service-status]');
          if (status) {
            const key = spec.status ? JSON.stringify(spec.status) : '';
            if (status.dataset.rk !== key) {
              status.dataset.rk = key;
              if (spec.status) paexFillTemplate(status, spec.status.t, spec.status.v, { tier: 'paex-avail__tiersum', lead: 'paex-avail__tiersum' });
              else status.textContent = '';
            }
          }
          setText('[data-service-detail]', spec.detail);
          const reason = setText('[data-service-reason]', '');
          if (reason) reason.hidden = true;
        });

        // lane A4 (visual review 3 / 13) → lane P5 (design LOC2): the Same-day row's "{postal}  Modify" unit under the
        // status whenever a postal is known and the store has the item (eligible or outside); the editor INSIDE the
        // row while the row is selected and no postal is known (design 1.6) or the shopper asked to change it
        // (showLocalEditor, prefilled)
        const localDest = box.querySelector('[data-local-dest]');
        const localStocked = localShown && !localPaused && storeQty > 0;
        if (localDest) {
          const showDest = localStocked && Boolean(local && local.postal);
          if (localDest.hidden !== !showDest) localDest.hidden = !showDest;
          const place = localDest.querySelector('[data-local-place]');
          const placeText = showDest ? local.postal : '';
          if (place && place.textContent !== placeText) place.textContent = placeText;
        }
        const postalBox = box.querySelector('[data-service-postal]');
        if (postalBox) {
          // Sasha 2026-10-23: the unit's Modify opens the editor whatever the row's state or selection (an "outside" row
          // cannot be selected, and the shopper must still be able to try another code)
          const showBox = !this.sheetAvailable() && localStocked && (this.localEditing || (selected === 'local' && (!local || local.state === 'prompt')));
          if (postalBox.hidden !== !showBox) postalBox.hidden = !showBox;
          if (!showBox) this.localEditing = false;
        }

        // lane A4 → lane P5 (design 1.9 / 1.9b): the grey info panel under the rows while a store service is paused —
        // the metaobject's pause_note when set, else ONE sentence from the strings: "Pickup and same-day aren’t
        // available right now." / "Pickup and same-day not available {range}." (both), or the one service named.
        // Nothing about Ship (the design dropped "Ship to home is still available."); never "busy" / a season in code.
        const note = box.querySelector('[data-service-note]');
        if (note) {
          let noteText = '';
          if (pickupPaused || localPaused) {
            noteText = pauseNote;
            if (!noteText) {
              const both = (pickupPaused || !pickupShown) && (localPaused || !localShown);
              const range = this.pauseRange(store, pickupPaused ? 'pickup' : 'local');
              if (both) noteText = (range && this.ft('pausedBothDates', { range })) || this.ft('pausedBoth', { store: name });
              else {
                const service = this.ft(pickupPaused ? 'servicePickup' : 'serviceLocal');
                noteText = (range && this.ft('pausedOneDates', { service, range })) || this.ft('pausedOne', { service });
              }
            }
          }
          const noteTarget = note.querySelector('[data-service-note-text]');
          if (noteTarget && noteTarget.textContent !== noteText) noteTarget.textContent = noteText;
          if (note.hidden !== !noteText) note.hidden = !noteText;
        }

        if (box.hidden) box.hidden = false;
        if (!this.hasAttribute('data-services')) this.setAttribute('data-services', '');
        if (this.getAttribute('data-selected-service') !== selected) this.setAttribute('data-selected-service', selected);
        this.syncRowLayout(shown); // lane A2: the tiles sit under their rows (one widget)
        this.renderShipDate(); // lane P5: "Ship to home · Arrives {date}" on the title line
        this.renderNudge();
        this.applyButton();
        this.stampPills();
      }

      /* ---------------- lane A2 (round 2, Sasha Oct 22 evening): ONE widget ----------------
         "on the PDP you have the 2 widgets, not one: the service rows AND the two stock availability tiles";
         "in the block there's no way to change the postal code"; "the shipping date doesn't show unless Ship is
         selected"; "in Pick up at store I want to change the store and view the inventory of other stores".
         While the selector is on, each tile's live summary (.paex-avail__summary-main: data-ship-answer +
         data-ship-channel with the clock-bound countdown · data-store-answer + data-store-channel with the Change
         store opener) is MOVED into its row's [data-service-live] slot and the whole tile <section> is moved right
         under that row, so the chevron body (Deliver-to editor, returns, order-by details · directions, nearby
         stores, recovery, Notify me) opens directly under the row. The tile's own summary bar is hidden by CSS
         ([data-in-row]); its renderers keep working because every lookup is element-wide or body-scoped. Off
         (the chip moves to a non-participating store): the nodes go back to their stable home (store tile first,
         ship tile after it) and the tiles render as on live — no clone, no lost listener. A store that offers
         same-day only (pickup off) hosts the store tile under its Same-day row (hostRowFor). Every move
         re-adds the ModalOpener listeners that global.js drops in disconnectedCallback (rebindOpeners), and
         the host delegates opener clicks as a fallback (delegateOpeners). */

      // the row's live summary slot (every row carries one when the store tile is rendered; the Same-day row's is
      // used only by a same-day-only store) — null when the row or the slot does not exist
      liveSlotFor(service) {
        const box = this.querySelector('[data-services-box]');
        const row = box && box.querySelector(`[data-service="${service}"]`);
        return row ? row.querySelector('[data-service-live]') : null;
      }

      // the tile a toggle controls: its own section, else the body named by aria-controls (the row chevrons)
      tileForToggle(toggle) {
        if (!toggle) return null;
        const own = toggle.closest('[data-tile]');
        if (own) return own;
        const id = toggle.getAttribute('aria-controls');
        const body = id && document.getElementById(id);
        return body ? body.closest('[data-tile]') : null;
      }

      // the service row the tile sits under (null while the tile sits in [data-tiles]); kept by placeTile, not
      // derived from aria-controls — the store body is named by BOTH the Pickup row's chevron and the Same-day
      // row's (a local-only store hosts the store tile under its Same-day row, review round 2)
      rowForTile(tile) {
        const placed = tile && this.rowPlaced && this.rowPlaced[tile.dataset.tile];
        return placed || null;
      }

      // true while the named tile sits under a service row (the row-shaped channel templates key on this, not
      // on data-services: a tile that stays home while the selector is on keeps its live wording)
      tileInRow(tileName) {
        return Boolean(this.rowPlaced && this.rowPlaced[tileName]);
      }

      // the row that hosts a tile while the selector is on: the ship tile under the Ship row, the store tile under
      // the Pickup row — or under the Same-day row when the store offers same-day only (pickup off): the store's
      // shelf is what same-day draws from, and the block keeps its inventory, nearby stores and Change store
      // instead of showing the tile as a second widget under the rows. null = the tile goes home.
      hostRowFor(tileName, shown) {
        if (!shown) return null;
        let service = null;
        if (tileName === 'ship' && shown.ship) service = 'ship';
        if (tileName === 'store') service = shown.pickup ? 'pickup' : (shown.local ? 'local' : null);
        if (!service) return null;
        const box = this.querySelector('[data-services-box]');
        const row = box && box.querySelector(`[data-service="${service}"]`);
        return row && row.querySelector('[data-service-live]') ? row : null;
      }

      // shown = { ship, pickup, local } while the selector is on; null = selector off (everything goes home).
      // Idempotent: called on every renderServiceRows pass.
      syncRowLayout(shown) {
        const box = this.querySelector('[data-services-box]');
        if (!box) return;
        this.rowHomes = this.rowHomes || {};
        const placed = this.rowPlaced || (this.rowPlaced = {});
        const tileNames = ['ship', 'store'];
        // the summary-main's home (its spot inside the tile's own summary bar) is captured BEFORE any move
        tileNames.forEach((tileName) => {
          const tile = this.querySelector(`[data-tile="${tileName}"]`);
          if (!tile || this.rowHomes[tileName]) return;
          const summary = tile.querySelector(':scope > .paex-avail__summary');
          const main = summary && summary.querySelector('.paex-avail__summary-main');
          this.rowHomes[tileName] = {
            parent: tile.parentNode,
            main,
            mainParent: main ? main.parentNode : null,
            mainNext: main ? main.nextSibling : null,
          };
        });
        let moved = false;
        // leave first (a tile under a row that no longer hosts it goes home — the store tile moves between the
        // Pickup and the Same-day row when the store's services change), then enter
        tileNames.forEach((tileName) => {
          const wanted = this.hostRowFor(tileName, shown);
          if (placed[tileName] && placed[tileName] !== wanted) {
            this.restoreTile(tileName);
            moved = true;
          }
        });
        tileNames.forEach((tileName) => {
          const wanted = this.hostRowFor(tileName, shown);
          if (wanted && !placed[tileName] && this.placeTile(tileName, wanted)) moved = true;
        });
        const tiles = this.querySelector('[data-tiles]');
        if (tiles) tiles.hidden = !tiles.querySelector('[data-tile]');
        if (moved) {
          // one body open at a time under the rows; collapsed when the tiles return (as on live: SSR renders
          // everything collapsed — a desktop both-open pair must never survive the move)
          this.querySelectorAll('[data-tile]').forEach((tile) => this.setTileExpanded(tile, false));
          this.syncOpenFlag();
          this.renderSummaries(); // the channel lines re-template for their new home (chord / no chord)
        }
      }

      // Moving a connected node fires its custom elements' disconnectedCallback; global.js's ModalOpener removes
      // its click / keydown listeners there and has no connectedCallback to add them back (review round 2
      // blocker: every Change store / Postal code opener in a moved subtree went dead). The bound handlers
      // survive as instance properties, so re-adding the SAME reference restores the opener exactly and is a
      // no-op on one that never lost them. Core change request: a connectedCallback in global.js ModalOpener.
      rebindOpeners(root) {
        if (!root || root.nodeType !== 1) return;
        const openers = Array.from(root.querySelectorAll('modal-opener'));
        if (root.matches && root.matches('modal-opener')) openers.unshift(root);
        openers.forEach((opener) => {
          if (typeof opener.boundOnOpenerClick === 'function') opener.addEventListener('click', opener.boundOnOpenerClick);
          if (typeof opener.boundOnKeyDown === 'function') opener.addEventListener('keydown', opener.boundOnKeyDown);
        });
      }

      // Belt and braces for the same blocker: an opener whose own listener is gone (or never bound — the drawer
      // was not in the document when the element upgraded) still opens its drawer. A live opener handles the
      // event first (target phase) and calls preventDefault, so the delegated handler steps back.
      delegateOpeners(signal) {
        const open = (event, opener) => {
          if (event.defaultPrevented || opener.getAttribute('aria-disabled') === 'true') return;
          if (opener.getAttribute('aria-expanded') === 'true') return;
          const drawer = document.getElementById(opener.getAttribute('aria-controls') || '');
          if (!drawer || typeof drawer.show !== 'function') return;
          event.preventDefault();
          drawer.show(opener);
        };
        this.addEventListener('click', (event) => {
          const opener = event.target && event.target.closest ? event.target.closest('modal-opener[aria-controls]') : null;
          if (opener && this.contains(opener)) open(event, opener);
        }, { signal });
        this.addEventListener('keydown', (event) => {
          if (event.code !== 'Space' && event.code !== 'Enter') return;
          const opener = event.target && event.target.closest ? event.target.closest('modal-opener[aria-controls]') : null;
          if (opener && this.contains(opener)) open(event, opener);
        }, { signal });
      }

      // the radio's description while its tile is placed: the live answer (the tier / the arrival date) ahead of
      // the row's own status · detail · reason — never the whole main column (the Change store opener and the
      // ticking countdown stay out of it, review round 2)
      describeRow(row, answerId) {
        const radio = row && row.querySelector('[data-service-radio]');
        if (!radio) return;
        if (!radio.dataset.describedbyBase) radio.dataset.describedbyBase = radio.getAttribute('aria-describedby') || '';
        const base = radio.dataset.describedbyBase;
        const value = [answerId, base].filter(Boolean).join(' ');
        if (radio.getAttribute('aria-describedby') !== value) radio.setAttribute('aria-describedby', value);
      }

      placeTile(tileName, row) {
        const home = this.rowHomes && this.rowHomes[tileName];
        const tile = this.querySelector(`[data-tile="${tileName}"]`);
        const live = row.querySelector('[data-service-live]');
        const caret = row.querySelector('[data-service-caret]');
        if (!home || !tile || !live) return false;
        if (home.main) {
          live.appendChild(home.main);
          this.rebindOpeners(home.main);
        }
        row.after(tile);
        this.rebindOpeners(tile);
        tile.setAttribute('data-in-row', '');
        if (caret) caret.hidden = false;
        if (tileName === 'ship') this.syncShipEditor(true);
        const answer = home.main && home.main.querySelector('.paex-avail__summary-answer');
        // lane A3: the Ship row's destination link (SSR'd in the row's main column) rides the moved summary right
        // after the answer — "… · Arrives {date} · G1P Change"; its home is remembered for restoreTile
        const dest = tileName === 'ship' && home.main ? row.querySelector('[data-ship-dest]') || this.querySelector('[data-ship-dest]') : null;
        if (dest) {
          if (!home.dest) home.dest = { node: dest, parent: dest.parentNode, next: dest.nextSibling };
          if (answer) answer.after(dest);
          else home.main.appendChild(dest);
        }
        this.describeRow(row, answer && answer.id ? answer.id : '');
        this.rowPlaced[tileName] = row;
        return true;
      }

      // home by STABLE anchors (review round 2): the store tile is always the first child of [data-tiles], the
      // ship tile follows it (or closes the container when the store tile is still out) — whatever the order
      // the tiles left in, the live anatomy comes back store-then-ship
      restoreTile(tileName) {
        const home = this.rowHomes && this.rowHomes[tileName];
        const tile = this.querySelector(`[data-tile="${tileName}"]`);
        if (!home || !tile || !home.parent) return;
        const row = this.rowForTile(tile);
        const caret = row && row.querySelector('[data-service-caret]');
        // lane A3: the destination link leaves the summary first — the tile's summary-main goes home as on live
        if (home.dest && home.dest.node && home.dest.parent) {
          const dest = home.dest.node;
          if (home.dest.next && home.dest.next.parentNode === home.dest.parent) home.dest.parent.insertBefore(dest, home.dest.next);
          else home.dest.parent.appendChild(dest);
          dest.hidden = true;
        }
        if (home.main && home.mainParent) {
          if (home.mainNext && home.mainNext.parentNode === home.mainParent) home.mainParent.insertBefore(home.main, home.mainNext);
          else home.mainParent.appendChild(home.main);
          this.rebindOpeners(home.main);
        }
        const container = home.parent;
        if (tileName === 'store') {
          container.insertBefore(tile, container.firstChild);
        } else {
          const storeTile = Array.from(container.childNodes).find((node) => node.nodeType === 1 && node.getAttribute('data-tile') === 'store');
          if (storeTile) storeTile.after(tile);
          else container.appendChild(tile);
        }
        this.rebindOpeners(tile);
        tile.removeAttribute('data-in-row');
        if (tileName === 'ship') this.syncShipEditor(false);
        if (row) {
          row.removeAttribute('data-body-open');
          this.describeRow(row, '');
        }
        if (caret) {
          caret.hidden = true;
          caret.setAttribute('aria-expanded', 'false');
        }
        this.rowPlaced[tileName] = null;
      }

      // lane A4 (visual review 6): under the Ship row the body holds the Deliver-to EDITOR itself (the row's
      // "· {place} Change" unit is the opener); home again, the tile shows its "Deliver to … · Change" line as on live
      syncShipEditor(inRow) {
        if (inRow && this.sheetAvailable()) inRow = false; // the sheet is the editor: the ship body keeps its "Deliver to … · Change" line
        const form = this.querySelector('[data-dest-form]');
        const opener = this.querySelector('[data-dest-open]');
        const input = this.querySelector('[data-dest-input]');
        const error = this.querySelector('[data-dest-error]');
        if (form && form.hidden !== !inRow) form.hidden = !inRow;
        if (opener && opener.hidden !== inRow) opener.hidden = inRow;
        if (!inRow) {
          if (input) input.removeAttribute('aria-invalid');
          if (error) error.textContent = '';
        }
      }

      // sold-out-online informational state (no selector): one line to the Deliver-to editor when a participating,
      // unpaused store has the variant — a mis-located shopper's way in (plan §3 proposed → accepted). Never for a
      // visitor with an explicitly chosen store: applyDest cannot promote past that choice (seams §2.7).
      renderNudge() {
        const nudge = this.querySelector('[data-fulfil-nudge]');
        if (!nudge) return;
        const button = nudge.querySelector('[data-fulfil-nudge-open]');
        let text = '';
        if (!this.hasAttribute('data-services') && this.fulfilApi() && !this.storeIsExplicit && this.variantId &&
            !this.onlineAvailable() && this.querySelector('[data-dest-open]')) {
          const candidate = this.inStockStores(null).find((row) => {
            const store = this.data.stores[row.storeId];
            return store && store.pickup === true && !this.pausedToday(store, 'pickup');
          });
          if (candidate) text = this.ft('nudgePostal', { store: this.data.stores[candidate.storeId].name || '' });
        }
        if (button && button.textContent !== text) button.textContent = text;
        nudge.hidden = !text;
      }

      ctaButton() {
        return document.getElementById(`Product-Submit-Button-${this.dataset.section}`);
      }

      // the native button state (label, disabled, quantity max) as the theme rendered it for the current variant —
      // from the variantChange payload (source html) or the live button before any override
      captureCtaNative(html) {
        const id = `Product-Submit-Button-${this.dataset.section}`;
        const lookup = html && typeof html.getElementById === 'function' ? html : null;
        const source = lookup ? lookup.getElementById(id) : null;
        const button = source || this.ctaButton();
        if (!button) return;
        const label = button.querySelector('.button-text');
        const qtyId = `Quantity-${this.dataset.section}`;
        const qty = (lookup ? lookup.getElementById(qtyId) : null) || document.getElementById(qtyId);
        let qtyMax = this.ctaNative ? this.ctaNative.qtyMax : null;
        if (qty && !qty.dataset.fulfilMax) qtyMax = qty.getAttribute('max');
        this.ctaNative = {
          disabled: button.hasAttribute('disabled'),
          label: label ? label.textContent.trim() : '',
          qtyMax,
        };
      }

      // the form's own variant id: paex-variant-fast writes it before the section round-trip lands
      formVariantId() {
        const form = document.getElementById(`product-form-${this.dataset.section}`);
        const input = form && form.querySelector('input[name="id"]');
        const value = input && Number(input.value);
        return value || this.variantId;
      }

      variantLabel() {
        const label = this.querySelector('[data-variant-label]');
        const text = label ? label.textContent.trim() : '';
        return text || this.variantTitle || '';
      }

      // Track 3.2 / 6c: the add-to-cart surface follows the service — label (add_pickup / add_local / the native
      // Add to cart for ship), disabled + "Not available for pickup in {variant}", quantity max = the store cap,
      // Buy it now hidden outside ship and for DC-out items. Without a selector the Track 3.1 gate still runs:
      // a DC-out variant (sellable at Québec per Shopify) must not be added to ship. Re-applied after every
      // native resync (variantChange subscriber + microtask, and the label observer for paex-variant-fast).
      applyButton() {
        const cta = this.ctaButton();
        if (!cta) return;
        if (!this.ctaNative) this.captureCtaNative(null);
        const section = this.dataset.section;
        const label = cta.querySelector('.button-text');
        const productForm = cta.closest('product-form');
        const dynamic = productForm ? productForm.querySelector('.dynamic-checkout') : null;
        const qty = document.getElementById(`Quantity-${section}`);
        const qtyLine = this.querySelector('[data-fulfil-qty]');
        const strings = window.variantStrings || {};
        // the engine's own "Unavailable" (an option combination with no variant: no variantChange is
        // published) is native territory — leave it alone
        const nativeUnavailable = Boolean(label && strings.unavailable && cta.hasAttribute('disabled') &&
          !cta.dataset.fulfilDisabled && label.textContent.trim() === String(strings.unavailable).trim());
        const nativeDisabled = Boolean(this.ctaNative && this.ctaNative.disabled) || nativeUnavailable;
        const variantId = this.formVariantId();
        const visible = this.hasAttribute('data-services');
        const service = visible ? (this.getAttribute('data-selected-service') || 'ship') : 'ship';
        let disable = nativeDisabled;
        let text = null;
        // Buy it now hides whenever the selected service is not ship (plan §3), the native state included
        let hideDynamic = visible && service !== 'ship';
        let cap = null;
        let postal = false;
        // The native disabled state (Shopify's own "Sold out" / "Unavailable") wins: no service label, no
        // quantity cap, no "Only N available for pickup" line, nothing logged. In pickup / local mode that is
        // the variant the store HAS on the shelf but Shopify cannot sell online yet — until Track 1.1 turns the
        // location on for online orders, variant.available stays false and /cart/add refuses the variant, so an
        // enabled "Add for pickup" button would fail; the Pickup row above still states the store stock. Once
        // the location fulfils online orders, variant.available turns true, the native button is enabled and
        // the normal pickup / local label + quantity cap below apply with no further change.
        if (!nativeDisabled) {
          if (service === 'pickup' || service === 'local') {
            hideDynamic = true;
            if (this.availableFor(service, variantId)) {
              // lane P5 (design copy.js): "Add for pickup" / "Add for same-day delivery" — no store name
              text = service === 'pickup' ? this.ft('addPickup') : this.ft('addLocal');
              cap = this.capFor(service, variantId);
            } else if (service === 'local' && this.localNeedsPostal(variantId)) {
              // design 1.6 / S.6: the button KEEPS "Add for same-day delivery" (muted, still clickable); a press
              // opens the row's postal field instead of adding until a code has been checked (guardCta)
              text = this.ft('addLocal');
              postal = true;
            } else {
              // lane P5 (design 1.8, changelog §3): the selected service can't be bought → "Not available"; the
              // longer reason stays on the sticky LINE (unavailableText, fulfilSticky)
              disable = true;
              text = this.ft('ctaUnavailable') || this.unavailableText(service, variantId);
            }
          } else if (!this.onlineAvailable(variantId)) {
            disable = true;
            hideDynamic = true;
            const variant = visible ? this.variantLabel() : '';
            text = (visible && this.ft('ctaUnavailable')) || (variant && this.ft('unavailableShipVariant', { variant })) || this.ft('soldOut') || strings.soldOut || null;
          }
        }
        this.ctaApplying = true;
        try {
          if (label) {
            if (text) {
              if (label.textContent !== text) label.textContent = text;
              cta.dataset.fulfilLabel = service;
            } else if (cta.dataset.fulfilLabel) {
              delete cta.dataset.fulfilLabel;
              const native = this.ctaNative ? this.ctaNative.label : '';
              if (native && label.textContent !== native) label.textContent = native;
              // Finish the look - PE owns the ship label again (its add-on count may be on)
              const addon = document.querySelector(`paex-addon[data-section="${section}"]`);
              if (addon && typeof addon.resyncCta === 'function') addon.resyncCta();
            }
          }
          if (postal) cta.dataset.fulfilPostal = '1';
          else if (cta.dataset.fulfilPostal) delete cta.dataset.fulfilPostal;
          if (disable) {
            if (!cta.hasAttribute('disabled')) cta.setAttribute('disabled', 'disabled');
            if (nativeDisabled) delete cta.dataset.fulfilDisabled;
            else cta.dataset.fulfilDisabled = '1';
          } else if (cta.dataset.fulfilDisabled) {
            delete cta.dataset.fulfilDisabled;
            cta.removeAttribute('disabled');
          }
          if (dynamic) dynamic.classList.toggle('paex-fulfil-hidden', hideDynamic);
          if (qty) {
            if (cap != null && cap > 0) {
              const capText = String(cap);
              if (qty.getAttribute('max') !== capText) {
                qty.setAttribute('max', capText);
                qty.dataset.max = capText;
              }
              qty.dataset.fulfilMax = '1';
              if (Number(qty.value) > cap) qty.value = capText;
            } else if (qty.dataset.fulfilMax) {
              delete qty.dataset.fulfilMax;
              const nativeMax = this.ctaNative ? this.ctaNative.qtyMax : null;
              if (nativeMax) {
                qty.setAttribute('max', nativeMax);
                qty.dataset.max = nativeMax;
              } else {
                qty.removeAttribute('max');
                delete qty.dataset.max;
              }
            }
          }
          // Sasha 2026-10-23: no "Only {n} available for pickup" sentence (removed in the design); the quantity cap stays on the input
          if (qtyLine && !qtyLine.hidden) { qtyLine.textContent = ''; qtyLine.hidden = true; }
        } finally {
          // the observer's records for our own writes are delivered before this microtask clears the flag
          Promise.resolve().then(() => { this.ctaApplying = false; });
        }
        this.syncStickyCta(cta);
        this.observeCta(cta, dynamic);
        this.guardCta(cta);
      }

      // same-day selected, the store has the item, no postal known yet (design 1.6): the add surfaces turn into the
      // way to the row's postal field
      localNeedsPostal(variantId) {
        const storeId = this.serviceStoreId();
        const store = storeId && this.data.stores[storeId];
        if (!store || store.local !== true || !variantId) return false;
        if (this.pausedToday(store, 'local') || this.qtyFor(variantId, storeId) <= 0) return false;
        return this.localInfo(store).state === 'prompt';
      }

      // the main button's press while it carries data-fulfil-postal: scroll to the Same-day row's postal field and
      // focus it instead of adding (capture — ahead of product-form's submit path); bound once per button element
      guardCta(cta) {
        if (!cta || cta.dataset.fulfilGuard) return;
        cta.dataset.fulfilGuard = '1';
        cta.addEventListener('click', (event) => {
          if (!cta.dataset.fulfilPostal) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.openLocalPostal();
        }, { capture: true, signal: this.abortController.signal });
      }

      // The disabled button's label, by REASON (design finding 45 — the size is named only when the size is
      // the reason): paused service → "Pickup not available right now" (no store, no size); the store has 0 of
      // THIS variant → "Not available for pickup in {variant}" (single-variant product: "Not in stock at {store}");
      // same-day without a postal → "Enter your postal code…"; postal outside the zone → "Not available for
      // same-day delivery at this address". Shared by the main CTA (applyButton) and the sticky line (fulfilSticky).
      unavailableText(service, variantId) {
        const storeId = this.serviceStoreId();
        const store = storeId && this.data.stores[storeId];
        const pickup = service === 'pickup';
        if (service === 'ship') {
          // lane P5: the sticky line's reason for a Ship pick that can't ship — the size when the size is the reason
          const variant = this.variantLabel();
          return (variant && this.ft('unavailableShipVariant', { variant })) || this.ft('shipOut') || this.ft('soldOut');
        }
        if (store && this.pausedToday(store, service)) {
          return this.ft(pickup ? 'ctaPausedPickup' : 'ctaPausedLocal') || this.ft(pickup ? 'cardPickupOut' : 'cardLocalOut');
        }
        if (!store || !variantId || this.qtyFor(variantId, storeId) <= 0) {
          const variant = this.variantLabel();
          const sized = variant ? this.ft(pickup ? 'unavailablePickupVariant' : 'unavailableLocalVariant', { variant }) : '';
          return sized || this.storeOutText(storeId) || this.ft(pickup ? 'cardPickupOut' : 'cardLocalOut');
        }
        if (!pickup) {
          const state = this.localInfo(store).state;
          if (state === 'prompt') return this.ft('localPostalMissing'); // no postal yet: the row's editor is the next step
          if (state === 'outside') return this.ft('ctaLocalOutside') || this.ft('cardLocalOut');
        }
        return this.ft(pickup ? 'cardPickupOut' : 'cardLocalOut');
      }

      // design findings 2 / 48 → lane P5 (design S.7, Sasha 2026-10-23): the sticky bar's Add mirrors the main
      // CTA's service-disabled state and is TRULY disabled — the single-variant bar button (type=submit name=add)
      // and the sized product's .mini-check-add alike (the reason sits in the line; the shopper picks another
      // service or size in the block above).
      syncStickyCta(cta) {
        const bar = document.getElementById(`Product-Mini-Checkout-${this.dataset.section}`);
        if (!bar) return;
        const main = cta || this.ctaButton();
        const mirror = Boolean(main && main.dataset.fulfilDisabled);
        bar.querySelectorAll('button[type="submit"][name="add"], .mini-check-add').forEach((button) => {
          if (mirror) {
            if (!button.hasAttribute('disabled')) button.setAttribute('disabled', 'disabled');
            button.dataset.paexFulfilDisabled = '1';
          } else if (button.dataset.paexFulfilDisabled) {
            delete button.dataset.paexFulfilDisabled;
            button.removeAttribute('disabled');
          }
        });
      }

      // paex-variant-fast repaints the label synchronously on a size change (before the variantChange publish);
      // any label / Buy-it-now write that is not ours re-applies the override on a microtask
      observeCta(cta, dynamic) {
        if (this.ctaObserver || !window.MutationObserver) return;
        this.ctaObserver = new MutationObserver(() => {
          if (this.ctaApplying || this.ctaQueued || !this.isConnected) return;
          this.ctaQueued = true;
          Promise.resolve().then(() => {
            this.ctaQueued = false;
            this.applyButton();
          });
        });
        this.ctaObserver.observe(cta, { childList: true, characterData: true, subtree: true });
        if (dynamic) this.ctaObserver.observe(dynamic, { attributes: true, attributeFilter: ['class'] });
        this.abortController.signal.addEventListener('abort', () => {
          if (this.ctaObserver) this.ctaObserver.disconnect();
          this.ctaObserver = null;
        }, { once: true });
      }

      // size pills: ONE combined unavailable state per service (6b: not two) — re-stamped after every
      // variantChange re-render of the picker and on every service change. Candidates per pill come from the
      // product .js payload (option values), else the snippet's data-paex-variant-id stamp.
      stampPills() {
        const picker = document.getElementById(`Variant-Selects-${this.dataset.section}`);
        if (!picker) return;
        const inputs = picker.querySelectorAll('.form-options-wrapper input[type="radio"]');
        if (!inputs.length) return;
        const service = this.hasAttribute('data-services') ? (this.getAttribute('data-selected-service') || 'ship') : 'ship';
        const groups = Array.from(picker.querySelectorAll('.product-form-input'));
        const chosen = groups.map((group) => {
          const field = group.querySelector('.form-options-wrapper input:checked, .form-options-wrapper select');
          return field && field.value ? field.value : null;
        });
        const variants = Array.isArray(this.liveVariants) ? this.liveVariants : null;
        inputs.forEach((input) => {
          const position = groups.indexOf(input.closest('.product-form-input'));
          let candidates = [];
          if (variants && position !== -1) {
            candidates = variants.filter((v) => Array.isArray(v.options) && v.options[position] === input.value &&
              v.options.every((option, index) => index === position || chosen[index] == null || option === chosen[index]));
            if (!candidates.length) candidates = variants.filter((v) => Array.isArray(v.options) && v.options[position] === input.value);
          } else if (input.dataset.paexVariantId) {
            candidates = [{ id: Number(input.dataset.paexVariantId) }];
          }
          const out = candidates.length > 0 && candidates.every((v) => !this.availableFor(service, v.id));
          input.classList.toggle('paex-fulfil-out', out);
          input.toggleAttribute('data-fulfil-out', out);
        });
      }

      // 6c: the PDP editor also writes edd_postal (the cart strip's attribute) through the bridge helper the mode
      // lane adds — bubble only, no drawer repaint while typing (seams §3.1); a no-op until the helper exists
      writeEddPostal(place, isCanadian) {
        const bridge = window.PaexCartBridge;
        if (!isCanadian || !bridge || typeof bridge.setAttributes !== 'function') return;
        try {
          const result = bridge.setAttributes({ edd_postal: place }, { drawer: null });
          if (result && typeof result.catch === 'function') result.catch(() => {});
        } catch (error) { /* additive */ }
      }

      // the sticky bar's mode line (assets/paex-sticky-delivery.js): null = no mode branch (today's date line, the
      // out-of-region bar — design S.8); { service, text, values?, tone?, unavailable?, postal?, key }:
      //   text       the line (a template when `values` is set — paexFillTemplate with the bar's date class)
      //   unavailable the line carries the REASON (tone 'warn') and the bar's Add is disabled (design S.7)
      //   postal     the bar's Add opens the row's postal field instead of adding (design S.6)
      // Lane P5 (design S.1–S.7, export4-script.js STK): ship "Arrives {date} · {place}", pickup "Pickup · ready in
      // 2 h" (DC out: "In store only · …"; store out: "Out of stock in store"), same-day "Same-day delivery · before
      // {cutoff}" / "Same-day · enter postal code". No store name in the bar.
      fulfilSticky() {
        if (!this.hasAttribute('data-services')) return null;
        const service = this.getAttribute('data-selected-service');
        if (service !== 'ship' && service !== 'pickup' && service !== 'local') return null;
        const unavailable = () => {
          const text = this.unavailableText(service, this.variantId);
          return { service, text, tone: 'warn', unavailable: true, key: `${service}|out|${text}` };
        };
        if (service === 'ship') {
          if (!this.availableFor('ship', this.variantId)) return unavailable();
          const estimate = this.estimate();
          if (!estimate || typeof paexEddFormatDate !== 'function') return null; // today's line decides (no date to show)
          const date = paexEddFormatDate(estimate.arrival);
          const place = this.destPlace() || '';
          const template = place ? this.ft('stickyShip') : this.ft('shipArrives');
          if (!template) return null;
          // Sasha 2026-10-23: the postal unit ("· G2K 0N2") is its own token — light ink like the tile status, only the date green
          return { service, text: template, values: { date, place: place ? '· ' + place : '' }, key: `ship|${date}|${place}` };
        }
        const storeId = this.serviceStoreId();
        const store = storeId && this.data.stores[storeId];
        if (!store) return null;
        const stocked = Boolean(this.variantId) && this.qtyFor(this.variantId, storeId) > 0;
        if (service === 'pickup') {
          if (!this.availableFor('pickup', this.variantId)) {
            // the store has none of this size → the design's own words; paused → the reason (unavailableText)
            if (!stocked && !this.pausedToday(store, 'pickup') && this.ft('stickyPickupOut')) {
              const text = this.ft('stickyPickupOut');
              return { service, text, tone: 'warn', unavailable: true, key: `pickup|out|${text}` };
            }
            return unavailable();
          }
          // the SHORT ready form (the bar is one line): readyLine().inline ("ready in 2 h" / "ready tomorrow around
          // noon") — never the full sentence; "In store only" when the DC is out (design S.4)
          const when = this.readyWhen(store);
          const ready = (when && when.short) || '';
          const storeOnly = !this.onlineAvailable(this.variantId) && this.ft('stickyPickupStoreOnly');
          const text = this.ft(storeOnly ? 'stickyPickupStoreOnly' : 'stickyPickup', { ready }).replace(/\s*·\s*$/, '');
          return { service, text, key: `pickup|${storeOnly ? 'store' : ''}|${ready}` };
        }
        const info = this.localInfo(store);
        // same order as the row (findings 1 / 44): paused, then the store's stock, then the postal prompt
        if (info.state === 'prompt' && stocked && !this.pausedToday(store, 'local')) {
          // design S.6: the bar's button keeps its own short label ("Add"), muted; its press opens the row's postal
          // field (openLocalPostal) — the sentence is the line's, never a button's
          return { service, text: this.ft('stickyLocalPrompt') || this.ft('localPostalMissing'), postal: true, key: 'local|prompt' };
        }
        if (!this.availableFor('local', this.variantId)) return unavailable();
        // "Same-day delivery · before 6 p.m." while the order can go today (localLine().cutoff); after the cutoff
        // the helper's next-day sentence rides the same lead ("Same-day delivery · delivered tomorrow around noon")
        if (info.today && info.cutoff && this.ft('stickyLocal')) {
          const text = this.ft('stickyLocal', { cutoff: info.cutoff });
          return { service, text, key: `local|${text}` };
        }
        const when = String(info.text || '');
        const text = this.ft('stickyLocalNext', { when: when.charAt(0).toLowerCase() + when.slice(1) }).replace(/\s*·\s*$/, '');
        return { service, text, key: `local|${text}` };
      }

      // the row's editor, open and prefilled with the postal the page already knows (the FSA the Ship row prints,
      // visual review 3) — shared by the status line's Change and the add surfaces (main button, sticky Add)
      showLocalEditor() {
        const box = this.querySelector('[data-services-box]');
        const postalBox = box && box.querySelector('[data-service-postal]');
        if (!postalBox) return null;
        this.localEditing = true;
        this.renderServices();
        const input = postalBox.querySelector('[data-local-input]');
        if (input && !input.value) {
          const known = this.localPostal();
          if (known) input.value = known;
        }
        return postalBox.hidden ? null : input;
      }

      // the main button / the sticky Add while a postal is missing land here: scroll to the Same-day row's own
      // editor and focus it
      openLocalPostal() {
        if (this.postalSheet(this.querySelector('[data-services-box] [data-local-open]') || this.querySelector('[data-services-box] [data-service="local"] label'))) return;
        const input = this.showLocalEditor();
        if (!input) {
          this.openPostalEditor();
          return;
        }
        if (typeof input.scrollIntoView === 'function') {
          try { input.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (error) { input.scrollIntoView(); }
        }
        input.focus({ preventScroll: true });
      }

      bindFulfil() {
        const signal = this.abortController.signal;
        const box = this.querySelector('[data-services-box]');
        if (box) {
          // Sasha 2026-10-23 (design item 10): the WHOLE tile selects the service — a click on the status half of
          // the row (under the label's overlay) picks it too. Links, buttons, inputs, the chevron, the opened
          // body ([data-tile]) and the postal editor keep their own action and never select.
          box.addEventListener('click', (event) => {
            const target = event.target;
            if (!target || !target.closest) return;
            if (target.closest('a, button, modal-opener, label, input, select, textarea, [role="button"], [data-service-caret], [data-service-postal], [data-tile], .paex-avail__tile')) return;
            const row = target.closest('[data-service]');
            if (!row || !box.contains(row) || row.hidden) return;
            const radio = row.querySelector('[data-service-radio]');
            if (!radio || radio.checked || radio.disabled || radio.getAttribute('aria-disabled') === 'true' || row.getAttribute('aria-disabled') === 'true') return;
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true })); // the change handler below does the rest
          }, { signal });
          box.addEventListener('change', (event) => {
            const radio = event.target.closest('[data-service-radio]');
            if (!radio || !box.contains(radio)) return;
            const service = radio.value;
            if (radio.getAttribute('aria-disabled') === 'true') {
              // an unavailable row cannot be chosen (its reason is still read) — the current choice stands
              const current = this.getAttribute('data-selected-service');
              const back = current && box.querySelector(`[data-service-radio][value="${current}"]`);
              if (back) back.checked = true;
              return;
            }
            this.selectedService = service;
            this.serviceChosen = true;
            this.localEditing = false; // a new pick closes a Change-opened editor; the prompt state reopens it
            const api = this.fulfilApi();
            if (api && typeof api.select === 'function') {
              // page-local choice (no write) → paex:fulfil-select for the sticky bar / quick-add
              try { api.select(service, this.serviceStoreId()); } catch (error) { /* the page choice still applies */ }
            }
            this.pushAnalytics('fulfil_selected', { service });
            this.renderServices();
          }, { signal });

          const localOpen = box.querySelector('[data-local-open]');
          const localForm = box.querySelector('[data-local-form]');
          const localInput = box.querySelector('[data-local-input]');
          const localError = box.querySelector('[data-local-error]');
          if (localOpen && localForm && localInput) {
            localOpen.addEventListener('click', () => {
              if (this.postalSheet(localOpen)) return;
              const input = this.showLocalEditor();
              if (input) input.focus();
            }, { signal });
            const closeLocal = () => {
              this.localEditing = false;
              localInput.removeAttribute('aria-invalid');
              if (localError) localError.textContent = '';
              this.renderServices();
            };
            // same keyup rule as the Deliver-to editor (the house ModalDialog closes on keyup)
            localInput.addEventListener('keydown', (event) => {
              if (event.key === 'Escape') event.stopPropagation();
            }, { signal });
            localInput.addEventListener('keyup', (event) => {
              if (event.key === 'Escape') {
                event.stopPropagation();
                closeLocal();
              }
            }, { signal });
            localForm.addEventListener('submit', (event) => {
              event.preventDefault();
              const raw = localInput.value.trim().toUpperCase();
              const region = typeof paexProvinceFromPostal === 'function' ? paexProvinceFromPostal(raw) : null;
              // a full postal or an FSA (the zone rules are prefix rules; the field opens prefilled with the known FSA)
              const fullPostal = /^[A-Z]\d[A-Z](\s?\d[A-Z]\d)?$/.test(raw);
              const valid = fullPostal && region && typeof paexPostalKeys === 'function' && paexPostalKeys(raw);
              if (!valid) {
                // lane P5 (design 1.6b): the same-day editor's own error (paex.fulfil.local_postal_error — a postal
                // code, no ZIP), the field keeps focus
                localInput.setAttribute('aria-invalid', 'true');
                if (localError) localError.textContent = this.ft('localPostalError') || this.data.texts.destError || '';
                localInput.focus();
                return;
              }
              const spaced = raw.replace(/\s+/g, '');
              const place = spaced.length > 3 ? `${spaced.slice(0, 3)} ${spaced.slice(3)}` : spaced;
              paexDestWrite({ region: region, place: place }); // paex:dest-change → applyDest + renderServices
              this.writeEddPostal(place, true);
              closeLocal();
            }, { signal });
          }
        }
        const nudge = this.querySelector('[data-fulfil-nudge-open]');
        if (nudge) nudge.addEventListener('click', () => this.openPostalEditor(), { signal });
        // the cart mode moved (set / cleared / boot): the carrier wins the selection again (seams §3.3)
        document.addEventListener('paex:fulfil-change', () => {
          this.serviceChosen = false;
          this.selectedService = null;
          this.renderServices();
        }, { signal });
      }

      isDesktop() {
        return window.matchMedia && window.matchMedia('(min-width: 750px)').matches;
      }

      setTileExpanded(tile, expanded) {
        if (!tile) return;
        tile.toggleAttribute('data-expanded', expanded);
        // every toggle that controls this body: the tile's own + the service row's chevron (lane A2)
        const body = tile.querySelector('[data-tile-body]');
        const toggles = body && body.id
          ? this.querySelectorAll(`[data-tile-toggle][aria-controls="${body.id}"]`)
          : tile.querySelectorAll('[data-tile-toggle]');
        toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false'));
        const row = tile.hasAttribute('data-in-row') ? this.rowForTile(tile) : null;
        if (row) row.toggleAttribute('data-body-open', expanded);
      }

      syncOpenFlag() {
        // desktop: [data-open] switches both tiles to the open anatomy
        this.toggleAttribute('data-open', Boolean(this.querySelector('[data-tile][data-expanded]')));
      }

      toggleTile(tile, userInitiated) {
        if (!tile) return;
        const expanded = tile.hasAttribute('data-expanded');
        const tiles = Array.from(this.querySelectorAll('[data-tile]'));
        // BOPIS (decision 6b-2): under the selector the tiles stack in one column
        // at every width and open one at a time, exactly as on mobile today
        if (this.isDesktop() && !this.hasAttribute('data-services')) {
          // design 2g: opening either one expands BOTH columns together —
          // the two halves never sit at different heights
          tiles.forEach((each) => this.setTileExpanded(each, !expanded));
        } else {
          // mobile (design 1h): one row open at a time
          tiles.forEach((each) => this.setTileExpanded(each, false));
          if (!expanded) this.setTileExpanded(tile, true);
        }
        this.syncOpenFlag();
        if (userInitiated && !expanded) {
          const channel = tile.dataset.tile;
          if (!this.expandFired[channel]) {
            this.expandFired[channel] = true;
            this.pushAnalytics('availability_expanded', { channel });
          }
        }
      }

      // Auto-open only when a row needs attention (design 2g): sold out
      // online with stock nearby, or the last shipping day. Else closed.
      autoExpand() {
        if (this.autoExpanded) return;
        if (this.pickupEnabled && !this.gateResolved) return; // decide once resolved
        const storeTile = this.querySelector('[data-tile="store"]');
        const shipTile = this.querySelector('[data-tile="ship"]');
        const online = this.onlineAvailable() ? 1 : 0; // M7: attention states follow sellability
        const storeQty = this.storeId && this.variantId ? this.qtyFor(this.variantId, this.storeId) : 0;
        const money = online <= 0 && storeQty > 0;
        let lastShipDay = false;
        if (online > 0 && this.inOccasion) {
          const region = this.destRegion();
          const estimate = this.estimate();
          // beforeCutoff, not shipsToday: with processing days the last ORDER
          // day still ends at the cutoff even though the parcel ships later
          if (region && estimate && estimate.beforeCutoff && typeof paexEddOrderBy === 'function') {
            const orderBy = paexEddOrderBy(region, this.data.settings.edd, this.data.settings.occasion.arriveBy);
            lastShipDay = Boolean(orderBy && orderBy.key === this.todayKey());
          }
        }
        this.autoExpanded = true;
        // SSR renders everything collapsed (Sasha 2026-07-27); the only
        // auto-open is the last order-by day — the deadline lives in the
        // ship row (approved design)
        if (lastShipDay) this.toggleTile(shipTile || storeTile, false);
        this.syncOpenFlag();
      }

      // B4 (round 2): same two channels as the size guide (paex-size-guide.js
      // track()): Shopify customer events `paex_store:<name>` for custom
      // pixels + the theme dataLayer (event names unchanged). Gated by the
      // store analytics setting; nothing in the editor.
      pushAnalytics(eventName, payload) {
        if (!this.analytics || window.Shopify?.designMode) return;
        const p = Object.assign({ theme_id: window.Shopify?.theme?.id || 0 }, payload);
        try {
          if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
            Shopify.analytics.publish('paex_store:' + eventName, p);
          }
        } catch (error) {
          /* additive */
        }
        try {
          (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: eventName }, p));
        } catch (error) {
          /* additive */
        }
      }

      // store_preselected — once per session per (method, store). No PII:
      // store id/name + rounded km only, never lat/lng/postal.
      // method: saved | rcc | account | postal | geo | gps | none
      reportPreselect(method, storeId, km) {
        if (!this.analytics || this.forceFootprint || window.Shopify?.designMode) return;
        if (method !== 'none' && !storeId) return;
        const key = `paex:preselect:${method}:${storeId || '-'}`;
        try {
          if (sessionStorage.getItem(key)) return;
          sessionStorage.setItem(key, '1');
        } catch (error) {
          // storage-less: once per element
          this.preselectSent = this.preselectSent || {};
          if (this.preselectSent[key]) return;
          this.preselectSent[key] = true;
        }
        const store = (storeId && this.data.stores[storeId]) || {};
        this.pushAnalytics('store_preselected', {
          store_id: storeId || null,
          store_name: store.name || null,
          method,
          distance_km: Number.isFinite(km) ? Math.round(km) : null
        });
      }

      // 'none' = in the footprint but no store after geo had its chance
      // (window load + 4 s: GeoTargetly + the /geo worker answer well before).
      // Lets store_preselected alone give the share: none vs the rest.
      schedulePreselectNone() {
        if (!this.analytics || this.forceFootprint || window.Shopify?.designMode) return;
        const arm = () => {
          this.preselectNoneTimer = setTimeout(() => {
            if (!this.isConnected || this.storeId || this.storeIsExplicit || !this.inFootprint) return;
            if (!this.gateResolved || this.postalPending) return;
            this.reportPreselect('none', null, null);
          }, 4000);
        };
        if (document.readyState === 'complete') arm();
        else window.addEventListener('load', arm, { once: true, signal: this.abortController.signal });
      }
    }
  );
}
